import React, { useEffect, useMemo, useRef, useState } from "react";
import "./HubMart.css";
import Template from "../main/template";
import Header from "./Header";
import Footer from "./Footer";
import { FaCamera, FaGift, FaLayerGroup, FaShieldAlt, FaPlus, FaSearch, FaSlidersH, FaSortAmountDownAlt, FaTimes } from "react-icons/fa";
import {
  useSession,
  SIGN_IN_ROUTE,
} from "./session";
/* =========================
   API + CONFIG
   ========================= */

const SEARCH_API_BASE = "https://api.goourhub.com:435";
const WRITE_API_BASE = "https://api.goourhub.com:435";
const BUSINESS_API_BASE = (
  process.env.REACT_APP_GOOURHUB_BUSINESS_API ||
  "https://business-api.goourhub.com"
).replace(/\/+$/, "");
const UPLOAD_URL = "https://api.goourhub.com:437/upload";
const IMAGE_CDN_BASE = "https://goourhub.sfo2.digitaloceanspaces.com/img/";
const PAGE_LIMIT = 10;
const MAX_IMAGES = 3;

// Bulk HubMart limits
const MAX_BULK_ITEMS = 25;

const BULK_ALLOWED_CATEGORIES = [
  "free",
  "cloth",
  "toys",
  "traditional",
];

const CITY_OPTIONS = ["San Antonio, TX"];

/* Hero backdrop photo, bundled by webpack from ./heroArt/*.jpg   the same
   folder and the same "file is the switch" rule as the hub hero:
     heroArt/sa-alamo.jpg present  -> the Alamo behind the marketplace hero
     file missing                  -> the plain white hero card, no build error
   Keep the image ~1.44:1 (e.g. 1400×971) with the façade on the right;
   tint, fade, and shade are tuned in HubMart.css (--sa-*). */
const tryHeroArt = (name) => {
  try {
    /* eslint-disable-next-line global-require, import/no-dynamic-require */
    return require(`./heroArt/${name}.jpg`);
  } catch {
    return null;
  }
};
const HERO_BACKDROP = tryHeroArt("sa-alamo");
const heroBackdropProps = (base) => ({
  className: HERO_BACKDROP ? `${base} hb-sa` : base,
  style: HERO_BACKDROP ? { "--sa-photo": `url(${HERO_BACKDROP})` } : undefined,
});
const PLACEHOLDER = `${process.env.PUBLIC_URL || ""}/img/hub-placeholder-320x240.png`;

/* API mapping from backend */
const categoryMap = {
  0: "free",
  1: "vehicle",
  2: "mobile",
  3: "computer",
  4: "job",
  5: "cloth",
  6: "rent",
  7: "house",
  8: "toys",
  9: "books",
  10: "other",
  11: "service",
  12: "traditional",
};

const CATEGORY_FREE = "free";
const VIRTUAL_CATEGORY_THREE = "everything at $3";

const ALL_CAT_IDS = Object.keys(categoryMap).map(Number);
const ALL_NON_FREE_CAT_IDS = ALL_CAT_IDS.filter((id) => id !== 0);

const ID_BY_CATEGORY = Object.fromEntries(
  Object.entries(categoryMap).map(([id, name]) => [name, Number(id)])
);

const CATEGORY_ORDER = [
  "free",
  "vehicle",
  "mobile",
  "computer",
  "job",
  "cloth",
  "traditional wear",
  "rent",
  "house",
  "toys",
  "books",
  "other",
  "service",
  
  VIRTUAL_CATEGORY_THREE,
];

const DISPLAY_LABELS = {
  free: "Free",
  [VIRTUAL_CATEGORY_THREE]: "Everything at $3",
  vehicle: "Vehicles",
  mobile: "Mobile",
  computer: "Computers",
  job: "Jobs",
  cloth: "Clothing",
  rent: "Rentals",
  house: "Homes",
  toys: "Toys",
  books: "Books",
  other: "Other",
  service: "Services",
  traditional: "Traditional Wear",
};

const CATEGORY_ICONS = {
  free: "🎁",
  vehicle: "🚗",
  mobile: "📱",
  computer: "💻",
  job: "💼",
  cloth: "👕",
  rent: "🏘️",
  house: "🏠",
  toys: "🧸",
  books: "📚",
  other: "✨",
  service: "🛠️",
  traditional: "🏺",
  [VIRTUAL_CATEGORY_THREE]: "💸",
};

const CATEGORY_ALIASES = {
  free: "free",
  freebies: "free",
  clothing: "cloth",
  clothes: "cloth",
  cloth: "cloth",
  vehicles: "vehicle",
  vehicle: "vehicle",
  cars: "vehicle",
  car: "vehicle",
  phones: "mobile",
  phone: "mobile",
  mobile: "mobile",
  computers: "computer",
  computer: "computer",
  jobs: "job",
  job: "job",
  rentals: "rent",
  rental: "rent",
  rent: "rent",
  homes: "house",
  houses: "house",
  house: "house",
  toys: "toys",
  toy: "toys",
  books: "books",
  book: "books",
  other: "other",
  services: "service",
  service: "service",
  traditional: "traditional",
  "traditional wear": "traditional",
"traditional wears": "traditional",
"traditional clothing": "traditional",


  [VIRTUAL_CATEGORY_THREE]: VIRTUAL_CATEGORY_THREE,
};

const CONDITION_OPTIONS = [
  "New",
  "Like New",
  "Excellent",
  "Good",
  "Fair",
  "Needs work",
  "Refurbished",
  "Used",
  "Open box",
  "Acceptable",
  "Fixer",
  "For parts",
  "New with tags",
  "New without tags",
];

const AI_CATEGORY_GENERIC_TOKENS = {
  free: ["free", "freebie", "freebies", "stuff"],
  vehicle: ["vehicle", "vehicles", "car", "cars"],
  mobile: ["mobile", "phone", "phones", "smartphone", "smartphones"],
  computer: ["computer", "computers", "laptop", "laptops"],
  job: ["job", "jobs", "work", "employment"],
  cloth: [
    "cloth",
    "clothes",
    "clothing",
    "dress",
    "dresses",
    "shirt",
    "shirts",
  ],
  traditional: ["traditional", "wear", "clothing", "clothes"],
  rent: ["rent", "rental", "rentals"],
  house: ["house", "houses", "home", "homes"],
  toys: ["toy", "toys"],
  books: ["book", "books"],
  other: ["other", "item", "items"],
  service: ["service", "services"],
};

// Route specific traditional garments deterministically. The AI can
// occasionally classify a saree as generic Clothing, but HubMart stores
// these listings in category 12 (Traditional Wear).
const AI_TRADITIONAL_WEAR_TERMS = new Set([
  "abaya",
  "abayas",
  "dashiki",
  "dashikis",
  "dhoti",
  "dhotis",
  "dupatta",
  "dupattas",
  "kaftan",
  "kaftans",
  "kente",
  "kimono",
  "kimonos",
  "kurta",
  "kurtas",
  "kurti",
  "kurtis",
  "lehenga",
  "lehengas",
  "qipao",
  "salwar",
  "salwars",
  "saree",
  "sarees",
  "sari",
  "saris",
  "sherwani",
  "sherwanis",
]);

const shouldUseTraditionalWear = (query) => {
  const normalized = String(query || "").toLowerCase();
  const words = normalized.match(/[a-z]+/g) || [];

  return (
    words.some((word) => AI_TRADITIONAL_WEAR_TERMS.has(word)) ||
    /\btraditional\s+(wear|clothes|clothing|dress|dresses|outfit|outfits)\b/.test(
      normalized
    )
  );
};

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "recommended", label: "Recommended" },
  { value: "price-asc", label: "Price: low to high" },
  { value: "price-desc", label: "Price: high to low" },
  { value: "title-asc", label: "Title: A to Z" },
  { value: "distance", label: "Closest first" },
];

const HERO_POINTS = [
  {
    icon: FaSearch,
    kicker: "Discover",
    title: "Local finds, less noise",
    text:
      "Browse focused San Antonio marketplace categories with search and filters that help you find the right item faster.",
      mobileText:
      "Search focused San Antonio categories with cleaner filters.",
    badge: "San Antonio first",
    tone: "violet",
  },
  {
  icon: FaLayerGroup,
  kicker: "List faster",
  title: "Post one or upload up to 25",

  text:
    "Signed-in sellers can upload up to 25 Free, Clothing, Traditional Wear, or Toys listings, with up to 3 photos for each item.",
    mobileText:
      "Post one item or use Multiple Upload for up to 25 listings.",
  badge: "Multiple Upload",
  tone: "teal",
},
  {
    icon: FaShieldAlt,
    kicker: "Stay in control",
    title: "Private, owner-managed listings",
    text:
      "Buyers can contact sellers through the private email flow, while signed-in owners can manage and delete their own listings.",
      mobileText:
      "Private buyer messages plus owner-only manage and delete controls.",
    badge: "Owner controls",
    tone: "amber",
  },
];
const createBulkItem = () => ({
  id: `${Date.now()}-${Math.random()}`,

  title: "",
  category: "free",
  price: "",
  condition: "",
  description: "",
  location: CITY_OPTIONS[0] || "",

  brand: "",
  gender: "",
  size: "",
  color: "",
  material: "",
  ageRange: "",

  images: [],
});
const defaultListingTemplate = {
  title: "",
  price: "",
  category: "",
  make: "",
  model: "",
  year: "",
  mileage: "",
  fuel_type: "",
  transmission: "",
  condition: "",
  description: "",
  location: CITY_OPTIONS[0] || "",
  zip: "",
  contactEmail: "",
  image: "",
  type: "item",
  lat: null,
  lon: null,
  contact: {
    emailRelay: true,
    showPhone: false,
    offeredInPerson: true,
    offeredVirtual: false,
  },
};

const CATEGORY_FIELDS = {
  vehicle: [
    { name: "make", label: "Make" },
    { name: "model", label: "Model" },
    { name: "year", label: "Year", type: "number" },
    { name: "mileage", label: "Mileage", type: "number" },
    { name: "fuel_type", label: "Fuel Type", placeholder: "Gasoline / Diesel / Hybrid / EV" },
    {
      name: "transmission",
      label: "Transmission",
      type: "select",
      options: ["Automatic", "Manual", "CVT", "Other"],
    },
    {
      name: "condition",
      label: "Condition",
      type: "select",
      options: ["New", "Like New", "Good", "Fair", "Needs work"],
    },
    { name: "vin", label: "VIN (optional)" },
    {
      name: "externalListingUrl",
      label: "Other site listing link (optional)",
      type: "url",
      placeholder: "https://...",
    },
  ],
  mobile: [
    { name: "brand", label: "Brand" },
    { name: "model", label: "Model" },
    { name: "storage", label: "Storage (GB)" },
    { name: "color", label: "Color" },
    { name: "carrier", label: "Carrier", placeholder: "Unlocked / AT&T / Verizon…" },
    {
      name: "condition",
      label: "Condition",
      type: "select",
      options: ["New", "Open box", "Excellent", "Good", "Fair"],
    },
    { name: "accessories", label: "Accessories", placeholder: "Charger, case, etc." },
  ],
  computer: [
    { name: "brand", label: "Brand" },
    {
      name: "device_type",
      label: "Type",
      type: "select",
      options: ["Laptop", "Desktop", "All-in-one", "Mini PC", "Other"],
    },
    { name: "cpu", label: "CPU" },
    { name: "ram", label: "RAM (GB)" },
    { name: "storage", label: "Storage (GB)" },
    { name: "gpu", label: "GPU (optional)" },
    { name: "os", label: "Operating System" },
    {
      name: "condition",
      label: "Condition",
      type: "select",
      options: ["New", "Refurbished", "Used", "For parts"],
    },
  ],
  job: [
    { name: "company", label: "Company or organization" },
    {
      name: "employmentType",
      label: "Employment type",
      type: "select",
      options: [
        "Full-time",
        "Part-time",
        "Contract",
        "Temporary",
        "Internship",
        "Seasonal",
      ],
    },
    {
      name: "salary",
      label: "Compensation",
      placeholder: "$18-$22/hour or $45,000-$55,000/year",
    },
    {
      name: "workArrangement",
      label: "Work arrangement",
      type: "select",
      options: ["On-site", "Hybrid", "Remote"],
    },
    {
      name: "applyUrl",
      label: "Application link (optional)",
      type: "url",
      placeholder: "https://...",
    },
  ],
  cloth: [
    { name: "brand", label: "Brand (optional)" },
    {
      name: "gender",
      label: "Gender",
      type: "select",
      options: ["Men", "Women", "Unisex", "Kids", "Baby"],
    },
    { name: "size", label: "Size" },
    { name: "color", label: "Color" },
    { name: "material", label: "Material (optional)" },
    {
      name: "condition",
      label: "Condition",
      type: "select",
      options: ["New with tags", "New without tags", "Like New", "Good", "Fair"],
    },
  ],
  rent: [
    {
      name: "propertyType",
      label: "Property Type",
      type: "select",
      options: ["Apartment", "House", "Townhome", "Condo", "Room", "Other"],
    },
    { name: "beds", label: "Beds", type: "number" },
    { name: "baths", label: "Baths", type: "number", step: "0.5" },
    { name: "sqft", label: "Square Feet", type: "number" },
    { name: "deposit", label: "Deposit", type: "number" },
    { name: "leaseTerm", label: "Lease Term", placeholder: "12 mo / 6 mo / flexible…" },
    { name: "availableDate", label: "Available", type: "date" },
    { name: "furnished", label: "Furnished", type: "checkbox" },
    { name: "pets", label: "Pets OK", type: "checkbox" },
    { name: "parking", label: "Parking (optional)" },
  ],
  house: [
    {
      name: "propertyType",
      label: "Property Type",
      type: "select",
      options: ["House", "Townhome", "Condo", "Multi-family", "Land", "Other"],
    },
    { name: "beds", label: "Beds", type: "number" },
    { name: "baths", label: "Baths", type: "number", step: "0.5" },
    { name: "sqft", label: "Square Feet", type: "number" },
    { name: "lotSize", label: "Lot Size (sqft)", type: "number" },
    { name: "yearBuilt", label: "Year Built", type: "number" },
    { name: "hoa", label: "HOA ($/mo)", type: "number" },
    { name: "parking", label: "Parking (optional)" },
    {
      name: "condition",
      label: "Condition",
      type: "select",
      options: ["New", "Like New", "Good", "Fair", "Fixer"],
    },
  ],
  toys: [
    { name: "brand", label: "Brand (optional)" },
    { name: "ageRange", label: "Age Range", placeholder: "3-5 yrs / 8+ / 14+…" },
    {
      name: "condition",
      label: "Condition",
      type: "select",
      options: ["New", "Like New", "Good", "Fair"],
    },
  ],
  books: [
    { name: "author", label: "Author" },
    { name: "isbn", label: "ISBN (optional)" },
    { name: "genre", label: "Genre (optional)" },
    {
      name: "condition",
      label: "Condition",
      type: "select",
      options: ["New", "Like New", "Good", "Acceptable"],
    },
  ],
  service: [
  {
    name: "serviceType",
    label: "Service type",
    placeholder: "Cleaning, tutoring, repair, photography…",
  },
  {
    name: "serviceArea",
    label: "Service area",
    placeholder: "San Antonio / Helotes / mobile service…",
  },
  {
    name: "availability",
    label: "Availability",
    placeholder: "Weekdays, weekends, evenings…",
  },
],

traditional: [
  {
    name: "brand",
    label: "Brand (optional)",
  },
  {
    name: "traditionalType",
    label: "Type / style",
    placeholder: "Saree, kurta, lehenga, kaftan, hanbok…",
  },
  {
    name: "gender",
    label: "For",
    type: "select",
    options: [
      "Men",
      "Women",
      "Unisex",
      "Kids",
      "Baby",
    ],
  },
  {
    name: "size",
    label: "Size",
  },
  {
    name: "color",
    label: "Color",
  },
  {
    name: "material",
    label: "Material (optional)",
  },
  {
    name: "condition",
    label: "Condition",
    type: "select",
    options: [
      "New with tags",
      "New without tags",
      "Like New",
      "Good",
      "Fair",
    ],
  },
],
  other: [
    {
      name: "condition",
      label: "Condition",
      type: "select",
      options: ["New", "Like New", "Good", "Fair", "Used"],
    },
  ],
};

const CATEGORY_FORM_CONFIG = {
  default: {
    titleLabel: "Title",
    titlePlaceholder: "Describe what you are listing",
    showPrice: true,
    priceRequired: true,
    priceLabel: "Price",
    pricePlaceholder: "0.00",
    descriptionLabel: "Description",
    descriptionHeading: "Tell buyers what matters most",
    descriptionPlaceholder:
      "Describe the condition, important details, pickup options, defects, and anything else the buyer should know.",
    photoHeading: "Upload strong visuals",
    photoHelp: "JPG, PNG, WEBP - good photos do half the selling for you.",
    contactHeading: "How buyers can reach you",
    previewLabel: "Buyer preview",
    publishLabel: "Publish listing",
  },
  free: {
    titlePlaceholder: "Free dining table - pickup in Helotes",
    showPrice: false,
    priceRequired: false,
    descriptionHeading: "Tell neighbors what matters most",
    descriptionPlaceholder:
      "Describe the item, condition, pickup location, pickup times, and anything the recipient should bring.",
    contactHeading: "How neighbors can reach you",
    previewLabel: "Neighbor preview",
  },
  vehicle: {
    titlePlaceholder: "2018 Toyota Camry SE, 82,000 miles",
    priceLabel: "Asking price",
    descriptionHeading: "Tell vehicle buyers what matters most",
    descriptionPlaceholder:
      "Describe the vehicle history, maintenance, condition, known issues, title status, and reason for selling.",
  },
  mobile: {
    titlePlaceholder: "Unlocked iPhone 15 Pro, 256GB",
    descriptionPlaceholder:
      "Include storage, carrier or unlocked status, battery condition, accessories, defects, and pickup details.",
  },
  computer: {
    titlePlaceholder: "Dell XPS 15, i7, 16GB RAM",
    descriptionPlaceholder:
      "Include processor, RAM, storage, operating system, battery condition, accessories, and known issues.",
  },
  job: {
    titleLabel: "Job title",
    titlePlaceholder: "Part-time Front Desk Assistant",
    showPrice: false,
    priceRequired: false,
    descriptionLabel: "Job description",
    descriptionHeading: "Tell job seekers what matters most",
    descriptionPlaceholder:
      "Describe the responsibilities, qualifications, work schedule, compensation, benefits, location, and how to apply.",
    photoHeading: "Company logo or workplace photos (optional)",
    photoHelp: "Add a logo or workplace photo if it helps applicants understand the opportunity.",
    contactHeading: "How applicants can reach you",
    previewLabel: "Job seeker preview",
    publishLabel: "Publish job",
  },
  cloth: {
    titlePlaceholder: "Women's denim jacket, size M",
    descriptionPlaceholder:
      "Include size, fit, brand, material, condition, measurements, and any flaws.",
  },
  traditional: {
    titlePlaceholder: "Men's embroidered kurta, size L",
    descriptionPlaceholder:
      "Include style, size, material, condition, measurements, cultural details, and any included accessories.",
  },
  rent: {
    titlePlaceholder: "Two-bedroom apartment near Medical Center",
    priceLabel: "Monthly rent",
    descriptionHeading: "Tell renters what matters most",
    descriptionPlaceholder:
      "Describe the property, lease terms, utilities, deposit, availability, parking, pet policy, and nearby landmarks.",
    contactHeading: "How renters can reach you",
    previewLabel: "Renter preview",
  },
  house: {
    titlePlaceholder: "Three-bedroom home for sale in Stone Oak",
    priceLabel: "Asking price",
    descriptionHeading: "Tell home buyers what matters most",
    descriptionPlaceholder:
      "Describe the home's layout, upgrades, condition, neighborhood, HOA, taxes, and showing availability.",
    contactHeading: "How home buyers can reach you",
    previewLabel: "Home buyer preview",
  },
  toys: {
    titlePlaceholder: "LEGO City set, ages 6+",
    descriptionPlaceholder:
      "Include the age range, condition, missing pieces, batteries, original packaging, and pickup details.",
  },
  books: {
    titlePlaceholder: "Organic Chemistry textbook, 9th edition",
    descriptionPlaceholder:
      "Include the author, edition, ISBN, condition, highlighting or notes, and whether access codes are included.",
  },
  other: {
    titlePlaceholder: "Oak bookshelf with five shelves",
    descriptionPlaceholder:
      "Explain exactly what the item is, its dimensions, condition, defects, and pickup details.",
  },
  service: {
    titleLabel: "Service title",
    titlePlaceholder: "Mobile car detailing in San Antonio",
    priceRequired: false,
    priceLabel: "Starting price or rate (optional)",
    descriptionLabel: "Service description",
    descriptionHeading: "Tell customers what matters most",
    descriptionPlaceholder:
      "Explain what is included, your service area, availability, experience, pricing, travel fees, and customer preparation.",
    contactHeading: "How customers can reach you",
    previewLabel: "Customer preview",
    publishLabel: "Publish service",
  },
};

/* =========================
   UTILS
   ========================= */

function normalizeCategoryName(value) {
  if (value === null || value === undefined || value === "") return "";

  const raw = String(value).trim().toLowerCase();

  if (/^\d+$/.test(raw)) {
    return categoryMap[Number(raw)] || "";
  }

  return CATEGORY_ALIASES[raw] || raw;
}

function getCategoryFormConfig(value) {
  const key = normalizeCategoryName(value);

  return {
    ...CATEGORY_FORM_CONFIG.default,
    ...(CATEGORY_FORM_CONFIG[key] || {}),
  };
}

function isValidHttpUrl(value) {
  if (!String(value || "").trim()) return true;

  try {
    const url = new URL(String(value).trim());
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function getListingPriceText(listing = {}) {
  const categoryKey = normalizeCategoryName(listing.category ?? listing.categoryId);

  if (categoryKey === "job") {
    return String(listing.salary || "").trim() || "Compensation not listed";
  }

  if (categoryKey === "service" && isFreePrice(listing.price)) {
    return "Contact for price";
  }

  if (categoryKey === "rent" && !isFreePrice(listing.price)) {
    return `${money(listing.price)} / month`;
  }

  return money(listing.price);
}

function shouldShowFreeBadge(listing = {}) {
  const categoryKey = normalizeCategoryName(listing.category ?? listing.categoryId);

  if (["job", "service"].includes(categoryKey)) return false;
  return isFreePrice(listing.price);
}

function getContactHeading(value) {
  const categoryKey = normalizeCategoryName(value);

  if (categoryKey === "job") return "Contact employer";
  if (categoryKey === "service") return "Contact service provider";
  if (categoryKey === "rent") return "Contact property owner";
  if (categoryKey === "house") return "Contact seller or agent";
  return "Contact seller";
}

function getContactRecipient(value) {
  const categoryKey = normalizeCategoryName(value);

  if (categoryKey === "job") return "employer";
  if (categoryKey === "service") return "service provider";
  if (categoryKey === "rent") return "property owner";
  if (categoryKey === "house") return "seller or agent";
  return "seller";
}

function getContactMessagePlaceholder(value) {
  const categoryKey = normalizeCategoryName(value);

  if (categoryKey === "job") {
    return "Hi, I'm interested in this position. Is it still open, and what are the next steps?";
  }

  if (categoryKey === "service") {
    return "Hi, I'm interested in this service. Could you share availability and an estimated price?";
  }

  if (categoryKey === "rent") {
    return "Hi, I'm interested in this rental. Is it still available, and when can I view it?";
  }

  return "Hi, I'm interested in this listing. Is it still available?";
}

function getSortOptionsForCategory(value, mobile = false) {
  const categoryKey = normalizeCategoryName(value);
  const hidePriceSort = [CATEGORY_FREE, "job", VIRTUAL_CATEGORY_THREE].includes(categoryKey);
  const base = mobile
    ? SORT_OPTIONS.filter((option) => ["newest", "price-asc", "price-desc"].includes(option.value))
    : SORT_OPTIONS;

  return hidePriceSort
    ? base.filter((option) => !option.value.startsWith("price-"))
    : base;
}

function getCategoryDetailRows(item = {}) {
  const categoryKey = normalizeCategoryName(item.category ?? item.categoryId);
  const fields = CATEGORY_FIELDS[categoryKey] || [];
  const skip = new Set(["condition", "applyUrl", "externalListingUrl"]);

  return fields
    .filter((field) => !skip.has(field.name))
    .map((field) => {
      const raw = item[field.name];

      if (field.type === "checkbox") {
        return raw ? { name: field.name, label: field.label, value: "Yes" } : null;
      }

      if (raw === null || raw === undefined || String(raw).trim() === "") return null;

      return {
        name: field.name,
        label: field.label,
        value: String(raw),
      };
    })
    .filter(Boolean);
}

function getListingValidationIssues(draft = {}, isSignedIn = false) {
  const categoryKey = normalizeCategoryName(draft.category);
  const formConfig = getCategoryFormConfig(categoryKey);
  const issues = [];

  const addIssue = (field, label, message) => {
    issues.push({ field, label, message });
  };

  if (!String(draft.title || "").trim()) {
    addIssue("title", "a title", "Add a title before publishing.");
  }

  if (!categoryKey || getCatIdByName(categoryKey) == null) {
    addIssue("category", "a category", "Choose a valid category.");
  }

  if (!draft.location) {
    addIssue("location", "a location", "Select a location.");
  }

  if (!String(draft.description || "").trim()) {
    addIssue(
      "description",
      categoryKey === "job" ? "a job description" : "a description",
      categoryKey === "job"
        ? "Add a job description so applicants understand the role."
        : "Add a description so people understand what you are offering."
    );
  }

  if (!isSignedIn) {
    const email = String(draft.contactEmail || "").trim();

    if (!/\S+@\S+\.\S+/.test(email)) {
      addIssue("contactEmail", "a valid contact email", "Add a valid contact email.");
    }
  }

  if (formConfig.priceRequired) {
    const price = Number(draft.price);

    if (draft.price === "" || !Number.isFinite(price) || price <= 0) {
      addIssue(
        "price",
        formConfig.priceLabel.toLowerCase(),
        `Enter a valid ${formConfig.priceLabel.toLowerCase()} greater than $0.`
      );
    }
  }

  if (categoryKey === "service" && String(draft.price || "").trim() !== "") {
    const price = Number(draft.price);

    if (!Number.isFinite(price) || price <= 0) {
      addIssue(
        "price",
        "a valid starting price or rate",
        "Enter a starting price greater than $0, or leave it blank for contact pricing."
      );
    }
  }

  if (categoryKey === "job") {
    if (!String(draft.company || "").trim()) {
      addIssue("company", "a company or organization", "Add the company or organization name.");
    }

    if (!String(draft.employmentType || "").trim()) {
      addIssue("employmentType", "an employment type", "Choose an employment type.");
    }

    if (!String(draft.salary || "").trim()) {
      addIssue("salary", "compensation", "Add the compensation or salary range.");
    }

    if (!String(draft.workArrangement || "").trim()) {
      addIssue("workArrangement", "a work arrangement", "Choose On-site, Hybrid, or Remote.");
    }
  }

  if (categoryKey === "job" && !isValidHttpUrl(draft.applyUrl)) {
    addIssue(
      "applyUrl",
      "a valid application link",
      "Enter a valid application link beginning with http:// or https://."
    );
  }

  if (categoryKey === "vehicle" && !isValidHttpUrl(draft.externalListingUrl)) {
    addIssue(
      "externalListingUrl",
      "a valid other-site link",
      "Enter a valid other-site vehicle link beginning with http:// or https://."
    );
  }

  return issues;
}

function buildListingPayload({
  draft,
  categoryName,
  ownerEmail,
  userLocation,
}) {
  const categoryKey = normalizeCategoryName(categoryName);
  const payload = {
    title: String(draft.title || "").trim(),
    category: categoryKey,
    description: String(draft.description || "").trim(),
    location: draft.location || CITY_OPTIONS[0] || "",
    zip: draft.zip || "",
    email: ownerEmail,
    contactEmail: ownerEmail,
    image: draft.image || "",
    type: draft.type || "item",
    lat: userLocation?.lat ?? null,
    lon: userLocation?.lon ?? null,
    seller: "User",
    contact: {
      ...defaultListingTemplate.contact,
      ...(draft.contact || {}),
    },
  };

  if (categoryKey === CATEGORY_FREE) {
    payload.price = 0;
  } else if (categoryKey !== "job" && String(draft.price || "").trim() !== "") {
    payload.price = Number(draft.price);
  }

  if (categoryKey === "job") {
    // Keep older readers safe while preventing Jobs from being displayed as Free.
    payload.hidePrice = true;
  }

  (CATEGORY_FIELDS[categoryKey] || []).forEach((field) => {
    const value = draft[field.name];

    if (field.type === "checkbox") {
      payload[field.name] = Boolean(value);
      return;
    }

    if (value !== null && value !== undefined && String(value).trim() !== "") {
      payload[field.name] = value;
    }
  });

  if (categoryKey === "job") {
    // Compatibility for older records/components that still read the former Remote checkbox.
    payload.remote = draft.workArrangement === "Remote";
  }

  return payload;
}

function getCatIdByName(name) {
  return ID_BY_CATEGORY[normalizeCategoryName(name)];
}

function categoryLabel(value) {
  const key = normalizeCategoryName(value);
  if (!key) return "Category";
  return DISPLAY_LABELS[key] || key.charAt(0).toUpperCase() + key.slice(1);
}

function categoryIcon(value) {
  return CATEGORY_ICONS[normalizeCategoryName(value)] || "•";
}

function isVirtualCategory(value) {
  return normalizeCategoryName(value) === VIRTUAL_CATEGORY_THREE;
}

function isFreePrice(raw) {
  if (raw === null || raw === undefined || raw === "") return true;
  const num = Number(raw);
  return !Number.isFinite(num) || num <= 0;
}

function money(value) {
  return isFreePrice(value) ? "Free" : `$${Number(value).toFixed(2)}`;
}

function safeHash() {
  return typeof window === "undefined" ? "" : window.location.hash || "";
}

function degreesToRadians(degrees) {
  return (Number(degrees) * Math.PI) / 180;
}

function distanceInMiles(lat1, lon1, lat2, lon2) {
  const earthRadiusMiles = 3958.8;
  const dLat = degreesToRadians(lat2 - lat1);
  const dLon = degreesToRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(degreesToRadians(lat1)) *
      Math.cos(degreesToRadians(lat2)) *
      Math.sin(dLon / 2) ** 2;

  return earthRadiusMiles * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function getSortLabel(value) {
  return SORT_OPTIONS.find((option) => option.value === value)?.label || "Newest first";
}

function normalizeIdValue(value) {
  if (!value) return "";

  if (typeof value === "object") {
    return String(value.$oid || value.oid || value.id || value._id || value.toString?.() || "");
  }

  return String(value);
}

function getBackendAdId(item) {
  const raw =
    item?.postid ||
    item?.postId ||
    item?.adid ||
    item?.adId ||
    item?.recordId ||
    item?._id ||
    item?.id ||
    item?.ID ||
    null;

  return normalizeIdValue(raw);
}

function getPaginationIdFromItem(item) {
  return (
    normalizeIdValue(item?.paginationId) ||
    normalizeIdValue(item?.paginationID) ||
    normalizeIdValue(item?.cursor) ||
    normalizeIdValue(item?.lastId) ||
    normalizeIdValue(item?.nextId) ||
    getBackendAdId(item)
  );
}

function stableId(item) {
  return (
    getBackendAdId(item) ||
    (item?.title
      ? item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 48)
      : String(Date.now()))
  );
}

function getListingTimestamp(item) {
  const raw =
    item?.createdAt ||
    item?.created_at ||
    item?.updatedAt ||
    item?.updated_at ||
    item?.postedAt ||
    item?.posted_at ||
    item?.postDate ||
    item?.postdate ||
    item?.date ||
    item?.timestamp ||
    item?.ts;

  const time = raw ? new Date(raw).getTime() : NaN;
  return Number.isFinite(time) ? time : 0;
}

function isPlaceholderUrl(url = "") {
  const value = String(url || "").trim();
  return !value || /via\.placeholder\.com/i.test(value) || /text=Preview/i.test(value);
}

function toCdnUrl(part) {
  if (!part) return "";

  let value = String(part).trim();

  try {
    if (/%7B|%5B|%7D|%5D/.test(value)) value = decodeURIComponent(value);
  } catch {
    // ignore
  }

  if (value.startsWith("[") || value.startsWith("{")) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return toCdnUrl(parsed[0]);
      if (Array.isArray(parsed?.files)) return toCdnUrl(parsed.files[0]);
    } catch {
      // ignore
    }
  }

  if (/^(https?:|blob:|data:)/i.test(value)) return value;
  if (value.startsWith("/")) return value;

  return IMAGE_CDN_BASE + value.replace(/^\/+/, "");
}

function toImageArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;

  if (typeof value === "string") {
    let text = value.trim();

    try {
      if (/%7B|%5B|%7D|%5D/.test(text)) text = decodeURIComponent(text);
    } catch {
      // ignore
    }

    if (text.startsWith("[") || text.startsWith("{")) {
      try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) return parsed;
        if (Array.isArray(parsed?.files)) return parsed.files;
      } catch {
        // ignore
      }
    }

    const match = text.match(/"files"\s*:\s*\[([^\]]+)\]/);

    if (match) {
      return match[1]
        .split(",")
        .map((part) => part.replace(/[\s"']/g, ""))
        .filter(Boolean);
    }

    return text
      .split(/[,\s]+/)
      .map((part) => part.trim())
      .filter(Boolean);
  }

  if (typeof value === "object") {
    const values = Object.values(value);
    if (values.every((item) => typeof item === "string")) return values;
  }

  return [];
}

function normalizeListingFromApi(row = {}) {
  const imageParts = []
    .concat(toImageArray(row.images))
    .concat(toImageArray(row.files))
    .concat(toImageArray(row.image_urls))
    .concat(toImageArray(row.gallery))
    .concat(toImageArray(row.image));

  ["image1", "image2", "image3", "file1", "file2", "file3", "img1", "img2", "img3"].forEach(
    (key) => {
      if (row[key]) imageParts.push(String(row[key]));
    }
  );

  const images = Array.from(new Set(imageParts.map(String)))
    .map(toCdnUrl)
    .filter((url) => url && !isPlaceholderUrl(url));

  const category = normalizeCategoryName(row.category ?? row.categoryId ?? row.cat ?? "");

  return {
    ...row,
    category,
    images,
    image: row.image && !isPlaceholderUrl(row.image) ? toCdnUrl(row.image) : images[0] || "",
    workArrangement:
      row.workArrangement ||
      row.work_arrangement ||
      (row.remote ? "Remote" : ""),
    applyUrl: row.applyUrl || row.apply_url || "",
    externalListingUrl:
      row.externalListingUrl ||
      row.external_listing_url ||
      row.otherSiteUrl ||
      row.other_site_url ||
      "",
  };
}

function listingLocationText(item) {
  return [item?.location, item?.city, item?.market, item?.address, item?.state, item?.zip]
    .filter(Boolean)
    .join(" ");
}

function cleanDedupeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function normalizedPriceForDedupe(value) {
  return isFreePrice(value) ? "0" : String(Number(value || 0));
}

function getListingDedupeKeys(item) {
  const backendId = getBackendAdId(item);

  // A real backend id uniquely identifies a listing, so rows that have
  // one only dedupe against the same id. Without this, two different
  // listings that happen to share a title and price (always "0" for
  // free items) silently collapse into one, and "Showing" drops below
  // the server's "Total".
  if (backendId) return [`id:${String(backendId)}`];

  const title = cleanDedupeText(item?.title);
  const description = cleanDedupeText(item?.description).slice(0, 140);
  const price = normalizedPriceForDedupe(item?.price);
  const category = normalizeCategoryName(item?.category ?? item?.categoryId ?? "");
  const location = cleanDedupeText(listingLocationText(item));
  const image = cleanDedupeText(item?.image || item?.images?.[0] || "");

  const keys = [];

  if (title && description) keys.push(`content:${category}|${title}|${description}|${price}`);
  if (title && location) keys.push(`title-location:${category}|${title}|${price}|${location}`);
  if (title && image) keys.push(`title-image:${category}|${title}|${price}|${image}`);
  if (title) keys.push(`title-price:${category}|${title}|${price}`);

  return keys.length ? keys : [`fallback:${JSON.stringify(item)}`];
}

function dedupeListings(items = []) {
  const seen = new Set();
  const output = [];

  for (const item of items || []) {
    const keys = getListingDedupeKeys(item);
    if (keys.some((key) => seen.has(key))) continue;

    keys.forEach((key) => seen.add(key));
    output.push(item);
  }

  return output;
}

function formatDistanceMiles(userLocation, listing) {
  if (
    userLocation?.lat == null ||
    userLocation?.lon == null ||
    listing?.lat == null ||
    listing?.lon == null
  ) {
    return "";
  }

  const miles = distanceInMiles(userLocation.lat, userLocation.lon, listing.lat, listing.lon);

  if (!Number.isFinite(miles)) return "";
  return miles < 1 ? "< 1 mi" : `${Math.round(miles)} mi away`;
}

function listingSearchText(item) {
  return [
    item?.title,
    item?.description,
    item?.seller,
    item?.brand,
    item?.make,
    item?.model,
    item?.condition,
    categoryLabel(item?.category),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function sortListings(items, sortBy, userLocation) {
  const arr = [...items];

  switch (sortBy) {
    case "newest":
      return arr.sort((a, b) => getListingTimestamp(b) - getListingTimestamp(a));
    case "price-asc":
      return arr.sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
    case "price-desc":
      return arr.sort((a, b) => Number(b.price || 0) - Number(a.price || 0));
    case "title-asc":
      return arr.sort((a, b) =>
        String(a.title || "").localeCompare(String(b.title || ""), undefined, {
          sensitivity: "base",
        })
      );
    case "distance":
      return arr.sort((a, b) => {
        const aMiles =
          userLocation?.lat != null &&
          userLocation?.lon != null &&
          a?.lat != null &&
          a?.lon != null
            ? distanceInMiles(userLocation.lat, userLocation.lon, a.lat, a.lon)
            : Number.POSITIVE_INFINITY;

        const bMiles =
          userLocation?.lat != null &&
          userLocation?.lon != null &&
          b?.lat != null &&
          b?.lon != null
            ? distanceInMiles(userLocation.lat, userLocation.lon, b.lat, b.lon)
            : Number.POSITIVE_INFINITY;

        return aMiles - bMiles;
      });
    default:
      return arr;
  }
}

function getRawResultArray(body) {
  if (Array.isArray(body)) return body;

  const candidates = [
    body?.result,
    body?.results,
    body?.items,
    body?.data?.result,
    body?.data?.results,
    body?.data?.items,
    body?.data,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }

  return [];
}

function getTotalCountFromBody(body) {
  const candidates = [
    body?.totalCount,
    body?.total_count,
    body?.totalcount,
    body?.totalItems,
    body?.total_items,
    body?.itemCount,
    body?.item_count,
    body?.total,
    body?.count,
    body?.data?.totalCount,
    body?.data?.total_count,
    body?.data?.totalcount,
    body?.data?.totalItems,
    body?.data?.total_items,
    body?.data?.itemCount,
    body?.data?.item_count,
    body?.data?.total,
    body?.data?.count,
  ];

  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined || candidate === "") continue;

    const num = Number(candidate);
    if (Number.isFinite(num)) return num;
  }

  return null;
}

function getPaginationCursorFromBody(body, items = []) {
  const candidates = [
    body?.id,
    body?.paginationId,
    body?.paginationID,
    body?.nextId,
    body?.nextID,
    body?.lastId,
    body?.lastID,
    body?.cursor,
    body?.nextCursor,
    body?.data?.id,
    body?.data?.paginationId,
    body?.data?.paginationID,
    body?.data?.nextId,
    body?.data?.lastId,
    body?.data?.cursor,
    body?.data?.nextCursor,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeIdValue(candidate);
    if (normalized) return normalized;
  }

  const lastItem = items[items.length - 1];
  return getPaginationIdFromItem(lastItem);
}

function getTotalPages(totalCount) {
  const total = Number(totalCount) || 0;
  if (total <= 0) return 1;
  return Math.max(1, Math.ceil(total / PAGE_LIMIT));
}

function createEmptyPagination(overrides = {}) {
  return {
    page: 1,
    totalCount: 0,
    totalPages: 1,
    requestKey: "",
    pages: {},
    cursors: { 1: "" },
    ...overrides,
  };
}

function makeRequestKey({
  categoryKey,
  query,
  priceMin,
  priceMax,
  condition,
}) {
  return JSON.stringify({
    category: normalizeCategoryName(categoryKey),
    query: String(query || "").trim(),
    priceMin: String(priceMin ?? ""),
    priceMax: String(priceMax ?? ""),
    condition: String(condition || "").trim(),
    limit: PAGE_LIMIT,
  });
}

function getVisiblePaginationPages(currentPage, totalPages) {
  const total = Math.max(1, Number(totalPages) || 1);
  const page = Math.min(Math.max(1, Number(currentPage) || 1), total);

  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }

  const pages = [1];

  if (page > 4) pages.push("start-ellipsis");

  const start = Math.max(2, page - 1);
  const end = Math.min(total - 1, page + 1);

  for (let item = start; item <= end; item += 1) {
    pages.push(item);
  }

  if (page < total - 3) pages.push("end-ellipsis");

  pages.push(total);

  return pages;
}

/* =========================
   API HELPERS
   ========================= */

async function readResponseBody(res) {
  const contentType = res.headers.get("content-type") || "";
  return contentType.includes("application/json") ? res.json() : res.text();
}
async function sendHubMartSelfNotification(
  sessionToken,
  payload
) {
  if (!sessionToken) {
    return false;
  }

  try {
    const response = await fetch(
      `${BUSINESS_API_BASE}/api/hubmart/notify-self`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${sessionToken}`,
        },
        body: JSON.stringify(payload),
      }
    );

    return response.ok;
  } catch (error) {
    console.warn(
      "HubMart notification could not be created:",
      error
    );

    /*
     * A notification problem must never make
     * a successful HubMart post fail.
     */
    return false;
  }
}
async function fetchListingsPage({
  categoryId,
  sentence = "",
  minPrice = "",
  maxPrice = "",
  condition = "",
  cursor = "",
}) {
  const params = new URLSearchParams();

  if (categoryId != null) {
    params.append("category", String(categoryId));
  }

  if (sentence) {
    params.append("sentence", String(sentence).trim());
  }

  // Go/Python search API expects min/max, NOT minPrice/maxPrice
  if (minPrice !== undefined && minPrice !== "") {
    params.append("min", String(minPrice));
  }

  if (maxPrice !== undefined && maxPrice !== "") {
    params.append("max", String(maxPrice));
  }

  if (condition) {
    params.append("condition", String(condition).trim());
  }

  params.append("ct", "1");
  params.append("limit", String(PAGE_LIMIT));

  const endpoint = cursor ? "/paginationSearch" : "/search";

  if (cursor) {
    params.append("id", String(cursor));
  }

  const res = await fetch(
    `${SEARCH_API_BASE}${endpoint}?${params.toString()}`
  );

  const body = await readResponseBody(res);

  if (!res.ok) {
    throw new Error(`Search failed: ${res.status}`);
  }

  const rawItems = getRawResultArray(body);

  const items = rawItems.map((row) => {
    const normalized = normalizeListingFromApi(row);

    const catId =
      categoryId != null
        ? Number(categoryId)
        : normalized.categoryId != null
        ? Number(normalized.categoryId)
        : getCatIdByName(normalized.category);

    const category =
      catId != null && categoryMap[catId]
        ? categoryMap[catId]
        : normalizeCategoryName(
            normalized.category ||
              row.category ||
              row.cat ||
              "other"
          );

    return {
      ...normalized,
      category,
      categoryId: catId,
    };
  });

  const totalCount = getTotalCountFromBody(body);
  const nextCursor = getPaginationCursorFromBody(body, items);

  return {
    items: dedupeListings(items),
    totalCount,
    totalPages:
      totalCount === null
        ? null
        : getTotalPages(totalCount),
    nextCursor,
    raw: body,
  };
}

async function fetchAllFromCategory(catId) {
  const page = await fetchListingsPage({ categoryId: catId });
  return page.items;
}

async function fetchEverything() {
  const chunks = await Promise.all(
    ALL_NON_FREE_CAT_IDS.map((id) => fetchAllFromCategory(id).catch(() => []))
  );

  return dedupeListings(chunks.flat());
}

async function insertListing(catId, payload, sessionToken = "") {
  const headers = {
    "Content-Type": "application/json",
  };

  if (sessionToken) {
    headers.Authorization = `Bearer ${sessionToken}`;
  }

  const res = await fetch(
    `${WRITE_API_BASE}/insert?category=${encodeURIComponent(catId)}&ct=1`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    }
  );

  const body = await readResponseBody(res);

  if (!res.ok) {
    throw new Error(
      `Insert failed: ${res.status} ${typeof body === "string" ? body : JSON.stringify(body)}`
    );
  }

  return body;
}

async function contactSeller(catId, payload) {
  const res = await fetch(
    `${WRITE_API_BASE}/contactSeller?category=${encodeURIComponent(Number(catId))}&ct=1`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const body = await readResponseBody(res);

  if (!res.ok) {
    throw new Error(`contactSeller failed: ${res.status}`);
  }

  return body;
}
async function deleteHubMartListing(
  catId,
  postId,
  sessionToken
) {
  const params = new URLSearchParams({
    id: String(postId),
    category: String(catId),
    ct: "1",
  });

  const res = await fetch(
    `${WRITE_API_BASE}/delete?${params.toString()}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      },
    }
  );

  const body = await readResponseBody(res);

  if (!res.ok) {
    throw new Error(
      typeof body === "string"
        ? body
        : body?.message ||
            `Delete failed: ${res.status}`
    );
  }

  return body;
}

function parseUploadResponse(raw) {
  if (raw && typeof raw === "object") {
    if (Array.isArray(raw.files)) return raw.files;
    if (Array.isArray(raw.data?.files)) return raw.data.files;
    if (Array.isArray(raw)) return raw;
  }

  if (typeof raw === "string") {
    const text = raw.trim();

    try {
      return parseUploadResponse(JSON.parse(text));
    } catch {
      // ignore
    }

    try {
      return parseUploadResponse(JSON.parse(decodeURIComponent(text)));
    } catch {
      // ignore
    }

    const match = text.match(/"files"\s*:\s*\[([^\]]+)\]/);

    if (match) {
      return match[1]
        .split(",")
        .map((part) => part.replace(/[\s"']/g, ""))
        .filter(Boolean);
    }

    return text ? [text] : [];
  }

  return [];
}

async function uploadImagesWithPostId(postId, files, catId) {
  const urls = [];

  for (const file of files.slice(0, MAX_IMAGES)) {
    const form = new FormData();
    form.append("postid", String(postId));
    form.append("category", String(catId));
    form.append("ct", "1");
    form.append("file", file, file.name);

    const url = `${UPLOAD_URL}?postid=${encodeURIComponent(postId)}&category=${encodeURIComponent(
      catId
    )}&ct=1`;

    const res = await fetch(url, { method: "POST", body: form });
    const raw = await res.text();

    if (!res.ok) {
      throw new Error(`Upload failed (${file.name}): ${res.status} ${raw}`);
    }

    parseUploadResponse(raw).forEach((key) => urls.push(toCdnUrl(key)));
  }

  return urls.filter((url) => url && !isPlaceholderUrl(url));
}

function saveImagesForPost(id, urls = []) {
  try {
    if (!id) return;

    const clean = Array.from(new Set(urls.filter(Boolean)));

    if (clean.length) {
      localStorage.setItem(`hb:imgs:${id}`, JSON.stringify(clean));
    }
  } catch {
    // ignore
  }
}

function loadImagesForPost(id) {
  try {
    if (!id) return [];

    const raw = localStorage.getItem(`hb:imgs:${id}`);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
}

function mergeImagesWithCache(listing = {}) {
  const id = getBackendAdId(listing) || stableId(listing);
  const apiImages = Array.isArray(listing.images) ? listing.images : [];
  const single = listing.image ? [listing.image] : [];

  // Server URLs first the localStorage cache is only a fallback, so a
  // stale/bad cached URL can never shadow a good one from the API.
  return Array.from(new Set([...apiImages, ...single, ...loadImagesForPost(id)].map(toCdnUrl))).filter(
    (url) => url && !isPlaceholderUrl(url)
  );
}

function attachCachedImages(items = []) {
  return items.map((item) => {
    const merged = mergeImagesWithCache(item);

    return merged.length
      ? {
          ...item,
          images: merged,
          image: merged[0] || item.image || "",
        }
      : item;
  });
}

/* =========================
   UI COMPONENTS
   ========================= */

/* Category-aware tips shown in the Listing Coach side panel. */
const STRENGTH_TIPS = {
  default: "Listings with photos and a clear description get far more replies.",
  free: "Free items move fast mention pickup times so people can grab it today.",
  vehicle: "Buyers filter by year and mileage fill in the vehicle details below.",
  mobile: "Note the carrier and storage those are the first two questions you'll get.",
  computer: "List CPU, RAM, and storage specs sell computers.",
  cloth: "Size and condition matter most for clothing double-check both.",
  rent: "Beds, baths, and the available date are what renters scan for first.",
  house: "Square footage and year built help serious buyers self-qualify.",
  toys: "Add the age range so parents can tell at a glance if it fits.",
  books: "The author and condition are what book buyers check first.",
  job: "Include pay job posts with pay listed get far more applicants.",
  service:
  "Be specific about what you offer, your service area, and when you're available.",

traditional:
  "Style, size, material, and condition help traditional wear buyers decide quickly.",
  other: "Specific titles beat generic ones: 'Oak bookshelf, 5-shelf' beats 'Furniture'.",
};

function buildListingChecklist(draft, imageCount, isSignedIn) {
  const categoryKey = normalizeCategoryName(draft.category);
  const formConfig = getCategoryFormConfig(categoryKey);
  const hasConditionField = (CATEGORY_FIELDS[categoryKey] || []).some(
    (field) => field.name === "condition"
  );

  const checks = [
    {
      key: "title",
      label: categoryKey === "job" ? "Clear job title (5+ characters)" : "Clear title (5+ characters)",
      done: (draft.title || "").trim().length >= 5,
      weight: 20,
    },
    {
      key: "category",
      label: "Category chosen",
      done: !!categoryKey && getCatIdByName(categoryKey) != null,
      weight: 14,
    },
    {
      key: "photos",
      label: `Photos added (${Math.min(imageCount, MAX_IMAGES)}/${MAX_IMAGES})`,
      done: imageCount > 0,
      progress: Math.min(imageCount / MAX_IMAGES, 1),
      weight: 26,
    },
    {
      key: "description",
      label: categoryKey === "job" ? "Job description (40+ characters)" : "Description (40+ characters)",
      done: (draft.description || "").trim().length >= 40,
      weight: 22,
    },
  ];

  if (categoryKey === CATEGORY_FREE) {
    checks.push({
      key: "price",
      label: "Free listing confirmed",
      done: true,
      weight: 8,
    });
  } else if (categoryKey === "job") {
    checks.push({
      key: "salary",
      label: "Compensation added",
      done: String(draft.salary || "").trim().length >= 3,
      weight: 8,
    });
  } else if (formConfig.priceRequired) {
    const price = Number(draft.price);

    checks.push({
      key: "price",
      label: `${formConfig.priceLabel} added`,
      done: draft.price !== "" && Number.isFinite(price) && price > 0,
      weight: 8,
    });
  }

  if (hasConditionField) {
    checks.push({
      key: "condition",
      label: "Condition selected",
      done: !!draft.condition,
      weight: 10,
    });
  }

  if (!isSignedIn) {
    checks.push({
      key: "email",
      label: "Contact email added",
      done: /\S+@\S+\.\S+/.test(String(draft.contactEmail || "")),
      weight: 10,
    });
  }

  const totalWeight = checks.reduce((sum, check) => sum + check.weight, 0);

  const earned = checks.reduce((sum, check) => {
    if (check.progress !== undefined) return sum + check.weight * check.progress;
    return sum + (check.done ? check.weight : 0);
  }, 0);

  const score = totalWeight ? Math.round((earned / totalWeight) * 100) : 0;

  return { checks, score };
}

function InlineStatus({ status }) {
  if (!status) return null;

  return (
    <div className={`hb-inline-status hb-inline-status--${status.type || "info"}`}>
      {status.text || status.msg}
    </div>
  );
}

function NoticeBanner({ notice, onClose }) {
  if (!notice) return null;

  return (
    <div className={`hb-banner hb-banner--${notice.type || "info"}`} role="status">
      <div className="hb-banner__content">
        <strong>{notice.type === "error" ? "Heads up:" : "Done:"}</strong>
        <span>{notice.text}</span>
      </div>
      <button type="button" className="hb-banner__close" onClick={onClose} aria-label="Dismiss">
        ×
      </button>
    </div>
  );
}

function ImageCarousel({
  images = [],
  alt = "",
  height = 220,
  fit = "cover",
  className = "",
  isPreview = false,
  emptyIcon = null,
  emptyTitle = "Photo unavailable",
  emptyText = "No working image is available for this listing.",
}) {
  const [index, setIndex] = useState(0);

  const [failedUrls, setFailedUrls] =
    useState(() => new Set());

  const touchX = useRef(null);

  /*
   * Normalize and deduplicate image URLs.
   * Do not insert PLACEHOLDER here.
   */
  const sourceItems = Array.from(
    new Set(
      (Array.isArray(images) ? images : [])
        .map(toCdnUrl)
        .filter(
          (url) =>
            url &&
            !isPlaceholderUrl(url)
        )
    )
  );

  /*
   * A string key is more stable than using
   * the array object as a dependency.
   */
  const sourceKey =
    sourceItems.join("|");

  useEffect(() => {
    setIndex(0);
    setFailedUrls(new Set());
  }, [sourceKey]);

  /*
   * URLs that fail in the browser are removed
   * from this carousel instance.
   */
  const items = sourceItems.filter(
    (url) => !failedUrls.has(url)
  );

  useEffect(() => {
    if (index >= items.length) {
      setIndex(
        Math.max(
          0,
          items.length - 1
        )
      );
    }
  }, [index, items.length]);

  const currentImage =
    items[index] || "";

  const move = (step) => {
    if (items.length < 2) {
      return;
    }

    setIndex(
      (previous) =>
        (
          previous +
          step +
          items.length
        ) %
        items.length
    );
  };

  const markImageFailed = (url) => {
    setFailedUrls((previous) => {
      const next =
        new Set(previous);

      next.add(url);

      return next;
    });
  };

  /*
   * No image render a designed component,
   * not a broken <img>.
   */
  if (!currentImage) {
    return (
      <div
        className={[
          "hb-carousel",
          "hb-carousel--empty",
          isPreview
            ? "is-preview"
            : "",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        style={{
          "--hb-carousel-height":
            `${height}px`,
        }}
        role="img"
        aria-label={emptyTitle}
      >
        <div className="hb-media-empty">
          <div
            className="hb-media-empty__icon"
            aria-hidden="true"
          >
            {emptyIcon || <FaCamera />}
          </div>

          <strong className="hb-media-empty__title">
            {emptyTitle}
          </strong>

          <span className="hb-media-empty__text">
            {emptyText}
          </span>

          {isPreview && (
            <div className="hb-media-empty__chips">
              <span>
                Cover photo first
              </span>

              <span>
                Up to {MAX_IMAGES}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`hb-carousel ${className}`.trim()}
      style={{
        "--hb-carousel-height":
          `${height}px`,
      }}
      onTouchStart={(event) => {
        touchX.current =
          event.touches?.[0]?.clientX ??
          null;
      }}
      onTouchEnd={(event) => {
        if (
          touchX.current == null
        ) {
          return;
        }

        const endX =
          event.changedTouches?.[0]
            ?.clientX ??
          touchX.current;

        const delta =
          endX - touchX.current;

        if (
          Math.abs(delta) > 30
        ) {
          move(
            delta < 0 ? 1 : -1
          );
        }

        touchX.current = null;
      }}
    >
      <img
        key={currentImage}
        className="hb-carousel__img"
        src={currentImage}
        alt={alt || "Listing photo"}
        style={{
          objectFit:
            fit === "contain" ||
            fit === "auto"
              ? "contain"
              : "cover",
        }}
        onError={() => {
          markImageFailed(
            currentImage
          );
        }}
      />

      {items.length > 1 && (
        <>
          <button
            type="button"
            className="hb-carousel__nav hb-carousel__nav--left"
            aria-label="Previous image"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              move(-1);
            }}
          >
            ‹
          </button>

          <button
            type="button"
            className="hb-carousel__nav hb-carousel__nav--right"
            aria-label="Next image"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              move(1);
            }}
          >
            ›
          </button>

          <div className="hb-carousel__footer">
            <div className="hb-carousel__dots">
              {items.map(
                (
                  item,
                  dotIndex
                ) => (
                  <span
                    key={`${dotIndex}-${item}`}
                    className={`hb-carousel__dot ${
                      dotIndex === index
                        ? "is-active"
                        : ""
                    }`}
                  />
                )
              )}
            </div>

            <span className="hb-carousel__count">
              {index + 1}/
              {items.length}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
function StatCard({
  icon: Icon,
  kicker,
  title,
  text,
  badge,
  tone = "violet",
}) {
  return (
    <article
      className={`hb-stat-card hb-stat-card--${tone}`}
    >
      <div
        className="hb-stat-card__icon"
        aria-hidden="true"
      >
        {Icon && <Icon />}
      </div>

      <div className="hb-stat-card__content">
        <div className="hb-stat-card__topline">
          <p className="hb-stat-card__kicker">
            {kicker}
          </p>

          {badge && (
            <span className="hb-stat-card__badge">
              {badge}
            </span>
          )}
        </div>

        <h3 className="hb-stat-card__title">
          {title}
        </h3>

        <p className="hb-stat-card__text">
          {text}
        </p>
      </div>
    </article>
  );
}

function MobileFeatureDeck() {
  return (
    <section
      className="hb-mobile-feature-deck"
      aria-labelledby="hb-mobile-feature-title"
    >
      <div className="hb-mobile-feature-deck__head">
        <div>
          <p className="hb-mobile-feature-deck__eyebrow">
            Why HubMart
          </p>

          <h2 id="hb-mobile-feature-title">
            More than a basic marketplace
          </h2>
        </div>

        <span className="hb-mobile-feature-deck__swipe">
          Swipe →
        </span>
      </div>

      <div className="hb-mobile-feature-deck__track">
        {HERO_POINTS.map((point) => {
          const Icon = point.icon;

          return (
            <article
              key={point.kicker}
              className={`hb-mobile-feature-card hb-mobile-feature-card--${
                point.tone || "violet"
              }`}
            >
              <div className="hb-mobile-feature-card__top">
                <div
                  className="hb-mobile-feature-card__icon"
                  aria-hidden="true"
                >
                  {Icon && <Icon />}
                </div>

                {point.badge && (
                  <span className="hb-mobile-feature-card__badge">
                    {point.badge}
                  </span>
                )}
              </div>

              <p className="hb-mobile-feature-card__kicker">
                {point.kicker}
              </p>

              <h3 className="hb-mobile-feature-card__title">
                {point.title}
              </h3>

              <p className="hb-mobile-feature-card__text">
                {point.mobileText || point.text}
              </p>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function ListingCard({
  listing,
  userLocation,
  isPreview = false,
}) {
  const images = mergeImagesWithCache(listing);
  const normalizedCategory = normalizeCategoryName(
    listing?.category ?? listing?.categoryId
  );

  const isJobListing = normalizedCategory === "job";

  const distance = formatDistanceMiles(userLocation, listing);

  const priceText = isJobListing
    ? ""
    : getListingPriceText(listing);

  const jobClosingDate =
    isJobListing && listing?.closingDate
      ? new Date(listing.closingDate).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "";

      if (normalizedCategory === "service" && !isPreview) {
  const provider =
    listing?.company ||
    listing?.seller ||
    "Local service provider";

  const serviceType =
    listing?.serviceCategory ||
    "Local Service";

  const pricing =
    listing?.pricingNote ||
    "Contact for pricing";

  return (
    <article className="hb-listing-card hb-listing-card--service">

      <div className="hb-service-card__header">
        <div className="hb-service-card__icon" aria-hidden="true">
          {categoryIcon("service")}
        </div>

        <div className="hb-service-card__identity">
          <span className="hb-service-card__category">
            {serviceType}
          </span>

          <strong className="hb-service-card__provider">
            {provider}
          </strong>
        </div>
      </div>

      <div className="hb-listing-card__body hb-service-card__body">
        <div>
          <h3 className="hb-listing-card__title">
            {listing?.title || "Service"}
          </h3>

          <p className="hb-listing-card__desc">
            {listing?.description ||
              "View this service for more information."}
          </p>
        </div>

        <div className="hb-service-card__meta">
          {listing?.location && (
            <span className="hb-meta-pill">
              📍 {listing.location}
            </span>
          )}

          {listing?.sourceVerified && (
            <span className="hb-meta-pill">
              ✓ Public source checked
            </span>
          )}
        </div>

        <div className="hb-service-card__pricing">
          {pricing}
        </div>

        <div className="hb-listing-card__cta">
          <span className="hb-listing-card__cta-copy">
            View service details
          </span>

          <span className="hb-link-arrow" aria-hidden="true">
            →
          </span>
        </div>
      </div>
    </article>
  );
}

  return (
    <article
      className={`hb-listing-card ${
        isPreview ? "hb-listing-card--preview" : ""
      } ${isJobListing ? "hb-listing-card--job" : ""}`}
    >
      {/* Photos are intentionally hidden for job listings */}
      {!isJobListing && (
        <div className="hb-listing-card__media">
          <ImageCarousel
            images={images}
            alt={listing?.title || "Listing"}
            height={232}
            fit="auto"
            isPreview={isPreview}
            emptyIcon={
              isPreview ? (
                <FaCamera />
              ) : (
                categoryIcon(normalizedCategory)
              )
            }
            emptyTitle={
              isPreview
                ? "Add your first photo"
                : "Photo unavailable"
            }
            emptyText={
              isPreview
                ? `Choose up to ${MAX_IMAGES} photos. The first photo becomes your cover image.`
                : `${categoryLabel(
                    normalizedCategory
                  )} listing - open to view the full details.`
            }
          />

          <div className="hb-listing-card__badges">
            {shouldShowFreeBadge(listing) && (
              <span className="hb-badge hb-badge--success">
                Free
              </span>
            )}

            {listing?.type === "featured" && (
              <span className="hb-badge hb-badge--warning">
                Featured
              </span>
            )}

            {normalizedCategory &&
              normalizedCategory !== CATEGORY_FREE && (
                <span className="hb-badge hb-badge--soft">
                  {categoryLabel(normalizedCategory)}
                </span>
              )}
          </div>
        </div>
      )}

      <div className="hb-listing-card__body">
        <div className="hb-listing-card__top">
          <div>
            {isJobListing && listing?.company && (
              <div className="hb-job-card__company">
                {listing.company}
              </div>
            )}

            <h3 className="hb-listing-card__title">
              {listing?.title || "Untitled listing"}
            </h3>

            <p className="hb-listing-card__desc">
              {listing?.description ||
                "No description provided yet."}
            </p>
          </div>

          {!isJobListing && (
            <div className="hb-listing-card__price">
              {priceText}
            </div>
          )}
        </div>

        {isJobListing ? (
          <div className="hb-listing-card__meta">
            {listing?.location && (
              <span className="hb-meta-pill">
                📍 {listing.location}
              </span>
            )}

            {listing?.employmentType && (
              <span className="hb-meta-pill">
                💼 {listing.employmentType}
              </span>
            )}

            {listing?.workArrangement && (
              <span className="hb-meta-pill">
                {listing.workArrangement}
              </span>
            )}

            {listing?.salary && (
              <span className="hb-meta-pill">
                💰 {listing.salary}
              </span>
            )}

            {jobClosingDate && (
              <span className="hb-meta-pill">
                📅 Apply by {jobClosingDate}
              </span>
            )}

            {listing?.externalJob &&
              listing?.verified && (
                <span className="hb-meta-pill">
                  ✓ External Job · Verified
                </span>
              )}
          </div>
        ) : (
          distance && (
            <div className="hb-listing-card__meta">
              <span className="hb-meta-pill">
                {distance}
              </span>
            </div>
          )
        )}

        {!isPreview && (
          <div className="hb-listing-card__cta">
            <span className="hb-listing-card__cta-copy">
              {isJobListing
                ? "View Job Details"
                : "Tap to view full details"}
            </span>

            <span
              className="hb-link-arrow"
              aria-hidden="true"
            >
              →
            </span>
          </div>
        )}
      </div>
    </article>
  );
}

function BulkPhotoThumb({ file, onRemove }) {
  // One object URL per file, revoked on unmount. Inline
  // URL.createObjectURL(file) in render mints a new leaked
  // blob URL on every keystroke in the bulk form.
  const url = useMemo(() => URL.createObjectURL(file), [file]);

  useEffect(() => {
    return () => URL.revokeObjectURL(url);
  }, [url]);

  return (
    <div className="hb-bulk-photo">
      <img src={url} alt="" />

      <button type="button" onClick={onRemove} aria-label="Remove photo">
        ×
      </button>
    </div>
  );
}

function ListingCoach({
  draft,
  imageCount,
  isSignedIn,
  previewListing,
  userLocation,
  isPosting,
  onPublish,
}) {
  const categoryKey = normalizeCategoryName(draft.category);
  const formConfig = getCategoryFormConfig(categoryKey);
  const { checks, score } = buildListingChecklist(draft, imageCount, isSignedIn);
  const tip = STRENGTH_TIPS[categoryKey] || STRENGTH_TIPS.default;
  const validationIssues = getListingValidationIssues(draft, isSignedIn);
  const missing = validationIssues.map((issue) => issue.label);
  const canPublish = validationIssues.length === 0;

  const headline =
    score >= 100
      ? "Ready to publish"
      : score >= 60
      ? "Almost there"
      : "Let's build your listing";

  return (
    <div className="hb-panel hb-panel--sticky hb-coach">
      <div className="hb-section-heading">
        <p className="hb-section-heading__eyebrow">Listing strength</p>
        <h2 className="hb-section-heading__title">{headline}</h2>
      </div>

      <div className="hb-coach__score">
        <div className="hb-coach__score-num">{score}%</div>
        <div className="hb-coach__bar">
          <div
            className={`hb-coach__bar-fill ${score < 40 ? "is-low" : ""}`.trim()}
            style={{ width: `${Math.max(score, 4)}%` }}
          />
        </div>
      </div>

      <ul className="hb-coach__list">
        {checks.map((check) => (
          <li
            key={check.key}
            className={`hb-coach__item ${check.done ? "is-done" : ""}`.trim()}
          >
            <span className="hb-coach__check" aria-hidden="true">✓</span>
            <span>{check.label}</span>
          </li>
        ))}
      </ul>

      <div className="hb-coach__tip">{tip}</div>

      <button
        type="button"
        className="hb-btn hb-btn--primary hb-btn--full"
        onClick={onPublish}
        disabled={isPosting || !canPublish}
      >
        {isPosting ? "Publishing..." : formConfig.publishLabel}
      </button>

      {!canPublish && (
        <div className="hb-coach__missing">To publish, add {missing.join(", ")}.</div>
      )}

      <div className="hb-coach__preview">
        <div className="hb-coach__preview-head">
          <p className="hb-coach__preview-label">{formConfig.previewLabel}</p>

          <span className="hb-preview-live">
            <span className="hb-preview-live__dot" aria-hidden="true" />
            Updates live
          </span>
        </div>

        <ListingCard
          listing={previewListing}
          userLocation={userLocation}
          isPreview
        />
      </div>
    </div>
  );
}

function Field({
  name,
  label,
  type = "text",
  options = [],
  value,
  onChange,
  placeholder,
  min,
  max,
  step,
}) {
  if (type === "checkbox") {
    return (
      <label className="hb-checkbox">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(event) => onChange(name, event.target.checked)}
        />
        <span>{label}</span>
      </label>
    );
  }

  if (type === "select") {
    const normalizedOptions = options.map((option) =>
      typeof option === "string" ? { value: option, label: option } : option
    );

    return (
      <div className="hb-form-group">
        <label className="hb-form-label" htmlFor={name}>
          {label}
        </label>
        <select
          className="hb-input"
          id={name}
          name={name}
          value={value ?? ""}
          onChange={(event) => onChange(name, event.target.value)}
        >
          <option value="">Select</option>
          {normalizedOptions.map((option) => (
            <option key={option.value || option.label} value={option.value}>
              {option.label || option.value}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (type === "textarea") {
    return (
      <div className="hb-form-group hb-form-group--full">
        <label className="hb-form-label" htmlFor={name}>
          {label}
        </label>
        <textarea
          className="hb-input hb-input--textarea"
          id={name}
          name={name}
          rows={5}
          placeholder={placeholder || ""}
          value={value ?? ""}
          onChange={(event) => onChange(name, event.target.value)}
        />
      </div>
    );
  }

  return (
    <div className="hb-form-group">
      <label className="hb-form-label" htmlFor={name}>
        {label}
      </label>
      <input
        className="hb-input"
        id={name}
        name={name}
        type={type}
        placeholder={placeholder || ""}
        min={min}
        max={max}
        step={step}
        value={value ?? ""}
        onChange={(event) => onChange(name, event.target.value)}
      />
    </div>
  );
}

function CategoryFields({ category, draft, onChange }) {
  const key = normalizeCategoryName(category);
  const fields = CATEGORY_FIELDS[key] || [];

  if (!fields.length) return null;

  return (
    <div className="hb-form-section">
      <div className="hb-section-heading">
        <p className="hb-section-heading__eyebrow">Category details</p>
        <h2 className="hb-section-heading__title">{categoryLabel(key)} specifics</h2>
      </div>
      <div className="hb-form-grid">
        {fields.map((field) => (
          <Field key={field.name} {...field} value={draft[field.name]} onChange={onChange} />
        ))}
      </div>
    </div>
  );
}

function ItemDetail({
  id,
  onBack,
  allItems,
  user,
  onDeleted,
  onRequireSignIn,
}) {
  const [item, setItem] = useState(null);
  const [buyerEmail, setBuyerEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sentStatus, setSentStatus] = useState(null);
const [deleting, setDeleting] =
  useState(false);

const [deleteStatus, setDeleteStatus] =
  useState(null);
  useEffect(() => {
    let found = null;

    try {
      const cached = sessionStorage.getItem(`hb:item:${id}`);
      if (cached) found = JSON.parse(cached);
    } catch {
      // ignore
    }

    if (!found) {
      found = allItems.find((listing) => String(stableId(listing)) === id) || null;
    }

    setItem(found);
  }, [id, allItems]);

  const handleContactSellerSubmit = async (event) => {
    event.preventDefault();

    if (!item) return;

    if (!buyerEmail.trim() || !message.trim()) {
      setSentStatus({ type: "error", msg: "Add your email and a message before sending." });
      return;
    }

    try {
      setSending(true);
      setSentStatus(null);

      const catId =
        item.categoryId != null ? Number(item.categoryId) : getCatIdByName(item.category) ?? 10;

      const backendAdId = getBackendAdId(item) || id;

      const response = await contactSeller(catId, {
        buyerEmail: buyerEmail.trim(),
        email: buyerEmail.trim(),
        adId: String(backendAdId),
        postid: String(backendAdId),
        id: String(backendAdId),
        content: message.trim(),
      });

      setSentStatus({
        type: "ok",
        msg: response?.message || `Your message has been sent to the ${getContactRecipient(item?.category ?? item?.categoryId)}.`,
      });
      setMessage("");
    } catch (err) {
      setSentStatus({
        type: "error",
        msg: err?.message || "Something broke while sending your message.",
      });
    } finally {
      setSending(false);
    }
  };

  const signedInEmail =
  String(user?.email || "")
    .trim()
    .toLowerCase();

const listingEmail =
  String(
    item?.email ||
      item?.contactEmail ||
      ""
  )
    .trim()
    .toLowerCase();

const isOwner =
  Boolean(
    signedInEmail &&
      listingEmail &&
      signedInEmail === listingEmail
  );

const handleDeleteListing = async () => {
  if (!user?.token) {
    onRequireSignIn?.();
    return;
  }

  if (!item || !isOwner) {
    setDeleteStatus({
      type: "error",
      msg: "You can only delete your own listing.",
    });
    return;
  }

  const confirmed =
    window.confirm(
      `Delete "${item.title || "this listing"}"?\n\nThis will permanently remove the listing and its uploaded photos.`
    );

  if (!confirmed) return;

  const catId =
    item.categoryId != null
      ? Number(item.categoryId)
      : getCatIdByName(item.category);

  const postId =
    getBackendAdId(item) || id;

  if (
    catId == null ||
    !postId
  ) {
    setDeleteStatus({
      type: "error",
      msg: "This listing could not be identified.",
    });
    return;
  }

  try {
    setDeleting(true);
    setDeleteStatus(null);

    await deleteHubMartListing(
      catId,
      postId,
      user.token
    );

    onDeleted?.(postId);
  } catch (err) {
    setDeleteStatus({
      type: "error",
      msg:
        err?.message ||
        "Could not delete this listing.",
    });
  } finally {
    setDeleting(false);
  }
};

  const images = item ? mergeImagesWithCache(item) : [];
  const itemCategoryKey = normalizeCategoryName(item?.category ?? item?.categoryId);
  const detailRows = item ? getCategoryDetailRows(item) : [];
  const contactHeading = getContactHeading(itemCategoryKey);
  const safeApplyUrl = item?.applyUrl && isValidHttpUrl(item.applyUrl) ? item.applyUrl : "";
  const safeExternalListingUrl =
    item?.externalListingUrl && isValidHttpUrl(item.externalListingUrl)
      ? item.externalListingUrl
      : "";
      const isJobDetail = itemCategoryKey === "job";
const isServiceDetail = itemCategoryKey === "service";

const servicePhone = String(
  item?.phone ||
  item?.contact?.phone ||
  ""
).trim();

const servicePhoneHref = servicePhone
  ? `tel:${servicePhone.replace(/[^\d+]/g, "")}`
  : "";

const rawServiceWebsite = String(
  item?.website ||
  item?.contact?.website ||
  ""
).trim();

const rawServiceWebsiteHost = String(
  item?.websiteHost ||
  ""
).trim();

const serviceWebsiteCandidate = rawServiceWebsite
  ? rawServiceWebsite
  : rawServiceWebsiteHost
    ? `https://${rawServiceWebsiteHost.replace(/^https?:\/\//i, "")}`
    : "";

const safeServiceWebsite =
  serviceWebsiteCandidate &&
  isValidHttpUrl(serviceWebsiteCandidate)
    ? serviceWebsiteCandidate
    : "";

const serviceCategoryName = String(
  item?.serviceCategory || ""
).trim();

const serviceArea = String(
  item?.serviceArea || ""
).trim();

const servicePricingNote = String(
  item?.pricingNote || ""
).trim();

const isExternalService =
  isServiceDetail && item?.externalService === true;

const isUnclaimedService =
  isServiceDetail &&
  String(item?.claimStatus || "").toLowerCase() === "unclaimed";
const safeSourceUrl =
  item?.sourceUrl && isValidHttpUrl(item.sourceUrl)
    ? item.sourceUrl
    : "";

const jobClosingDate = (() => {
  if (!isJobDetail || !item?.closingDate) return "";

  const date = new Date(item.closingDate);

  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
})();

  return (
    <div className="hb-detail-page">
      <div className="hb-detail-page__topbar">
        <button type="button" className="hb-btn hb-btn--ghost" onClick={onBack}>
          ← Back to marketplace
        </button>
      </div>

      {!item && <div className="hb-empty-state">Loading listing…</div>}

      {item && (
        <div className="hb-detail-shell">
          <section className="hb-detail-main">
            <div className="hb-detail-main__heading">
  <div className="hb-detail-main__eyebrow">
    {isJobDetail
      ? item.company || "Employment opportunity"
      : categoryLabel(item.category)}
  </div>

  <h1 className="hb-detail-main__title">
    {item.title}
  </h1>

  {isJobDetail ? (
    <div
      className="hb-listing-card__meta"
      style={{ marginTop: 14 }}
    >
      {item.location && (
        <span className="hb-meta-pill">
          📍 {item.location}
        </span>
      )}

      {item.employmentType && (
        <span className="hb-meta-pill">
          💼 {item.employmentType}
        </span>
      )}

      {item.workArrangement && (
        <span className="hb-meta-pill">
          {item.workArrangement}
        </span>
      )}

      {item.salary && (
        <span className="hb-meta-pill">
          💰 {item.salary}
        </span>
      )}

      {jobClosingDate && (
        <span className="hb-meta-pill">
          📅 Apply by {jobClosingDate}
        </span>
      )}
    </div>
  ) : (
    <div className="hb-detail-main__price">
      {getListingPriceText(item)}
    </div>
  )}
</div>

      {!isJobDetail && (
  <div className="hb-detail-card">
    <ImageCarousel
      images={images}
      alt={item.title}
      height={540}
      fit="auto"
      className="hb-detail-carousel"
      emptyIcon={categoryIcon(item.category)}
      emptyTitle="Photo unavailable"
      emptyText="This listing does not currently have a working photo."
    />
  </div>
)}

            <div className="hb-detail-card">
              <div className="hb-section-heading">
                <p className="hb-section-heading__eyebrow">Overview</p>
                <h2 className="hb-section-heading__title">Description</h2>
              </div>
              <p className="hb-detail-copy">{item.description || "No description provided yet."}</p>
            </div>
          </section>

          <aside className="hb-detail-sidebar">
            <div className="hb-detail-card hb-detail-card--sticky">
              <div className="hb-section-heading">
                <p className="hb-section-heading__eyebrow">Listing details</p>
                <h2 className="hb-section-heading__title">Quick facts</h2>
              </div>

            <div className="hb-info-list">
  {isJobDetail ? (
    <>
      <div className="hb-info-list__row">
        <span>Employer</span>
        <strong>
          {item.company || "Organization"}
        </strong>
      </div>

      {item.location && (
        <div className="hb-info-list__row">
          <span>Location</span>
          <strong>{item.location}</strong>
        </div>
      )}
      {isServiceDetail && serviceCategoryName && (
  <div className="hb-info-list__row">
    <span>Service</span>
    <strong>{serviceCategoryName}</strong>
  </div>
)}

{isServiceDetail && serviceArea && (
  <div className="hb-info-list__row">
    <span>Service area</span>
    <strong>{serviceArea}</strong>
  </div>
)}

{isServiceDetail && servicePhone && (
  <div className="hb-info-list__row">
    <span>Phone</span>

    <strong>
      <a href={servicePhoneHref}>
        {servicePhone}
      </a>
    </strong>
  </div>
)}

{isServiceDetail && servicePricingNote && (
  <div className="hb-info-list__row">
    <span>Pricing</span>
    <strong>{servicePricingNote}</strong>
  </div>
)}

{isServiceDetail && safeServiceWebsite && (
  <div className="hb-info-list__row">
    <span>Website</span>

    <strong>
      <a
        href={safeServiceWebsite}
        target="_blank"
        rel="noopener noreferrer"
      >
        Official website ↗
      </a>
    </strong>
  </div>
)}

{isExternalService && (
  <div className="hb-info-list__row">
    <span>Listing</span>
    <strong>
      {isUnclaimedService
        ? "External · Unclaimed"
        : "External service"}
    </strong>
  </div>
)}

{isServiceDetail && item?.sourceVerified && (
  <div className="hb-info-list__row">
    <span>Source</span>
    <strong>✓ Verified public source</strong>
  </div>
)}

      {item.employmentType && (
        <div className="hb-info-list__row">
          <span>Employment</span>
          <strong>{item.employmentType}</strong>
        </div>
      )}

      {item.workArrangement && (
        <div className="hb-info-list__row">
          <span>Work arrangement</span>
          <strong>{item.workArrangement}</strong>
        </div>
      )}

      {item.salary && (
        <div className="hb-info-list__row">
          <span>Salary</span>
          <strong>{item.salary}</strong>
        </div>
      )}

      {jobClosingDate && (
        <div className="hb-info-list__row">
          <span>Apply by</span>
          <strong>{jobClosingDate}</strong>
        </div>
      )}

      {item.externalJob && (
        <div className="hb-info-list__row">
          <span>Listing</span>
          <strong>
            {item.verified
              ? "✓ External Job · Verified"
              : "External Job"}
          </strong>
        </div>
      )}

      {item.sourceName && (
        <div className="hb-info-list__row">
          <span>Source</span>

          <strong>
            {safeSourceUrl ? (
              <a
                href={safeSourceUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {item.sourceName}
              </a>
            ) : (
              item.sourceName
            )}
          </strong>
        </div>
      )}
    </>
  ) : (
    <>
      <div className="hb-info-list__row">
        <span>Seller</span>
        <strong>{item.seller || "User"}</strong>
      </div>

      {item.location && (
        <div className="hb-info-list__row">
          <span>Location</span>
          <strong>{item.location}</strong>
        </div>
      )}

      {item.condition && (
        <div className="hb-info-list__row">
          <span>Condition</span>
          <strong>{item.condition}</strong>
        </div>
      )}

      {item.category && (
        <div className="hb-info-list__row">
          <span>Category</span>
          <strong>{categoryLabel(item.category)}</strong>
        </div>
      )}

      {detailRows.map((row) => (
        <div
          className="hb-info-list__row"
          key={row.name}
        >
          <span>{row.label}</span>
          <strong>{row.value}</strong>
        </div>
      ))}
    </>
  )}
</div>

              {(safeApplyUrl || safeExternalListingUrl) && (
                <div className="hb-form-stack" style={{ marginTop: 18 }}>
                  {safeApplyUrl && itemCategoryKey === "job" && (
                    <a
                      className="hb-btn hb-btn--primary hb-btn--full"
                      href={safeApplyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Apply on Employer Site →
                    </a>
                  )}

                  {safeExternalListingUrl && itemCategoryKey === "vehicle" && (
                    <a
                      className="hb-btn hb-btn--ghost hb-btn--full"
                      href={safeExternalListingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View vehicle on other site
                    </a>
                  )}
                </div>
              )}
              {isOwner && (
  <div
    style={{
      marginTop: 18,
    }}
  >
    <button
      type="button"
      className="hb-btn hb-btn--ghost hb-btn--full"
      onClick={handleDeleteListing}
      disabled={deleting}
      style={{
        color: "#b42318",
      }}
    >
      {deleting
        ? "Deleting…"
        : "Delete my listing"}
    </button>

    {deleteStatus && (
      <div
        className={`hb-inline-status hb-inline-status--${
          deleteStatus.type || "info"
        }`}
      >
        {deleteStatus.msg}
      </div>
    )}
  </div>
)}
{isExternalService && (
  <div className="hb-detail-card">
    <div className="hb-section-heading">
      <p className="hb-section-heading__eyebrow">
        Contact
      </p>

      <h2 className="hb-section-heading__title">
        Contact service provider
      </h2>
    </div>

    <div className="hb-form-stack">
      {servicePhone && (
        <a
          className="hb-btn hb-btn--primary hb-btn--full"
          href={servicePhoneHref}
        >
          Call {servicePhone}
        </a>
      )}

      {safeServiceWebsite && (
        <a
          className="hb-btn hb-btn--ghost hb-btn--full"
          href={safeServiceWebsite}
          target="_blank"
          rel="noopener noreferrer"
        >
          Visit Provider Website →
        </a>
      )}

      {item?.address && (
        <div className="hb-inline-status hb-inline-status--info">
          {item.address}
        </div>
      )}

      <p
        style={{
          margin: 0,
          fontSize: "0.82rem",
          lineHeight: 1.5,
          color: "#667085",
        }}
      >
        External, unclaimed service listing. GoOurHub is not
        affiliated with or endorsed by this provider. Please verify
        services, availability and pricing directly with the provider.
      </p>
    </div>
  </div>
)}
            </div>
{!isExternalService && (
            <div className="hb-detail-card">
              <div className="hb-section-heading">
                <p className="hb-section-heading__eyebrow">Reach out</p>
                <h2 className="hb-section-heading__title">{contactHeading}</h2>
              </div>

              <form className="hb-form-stack" onSubmit={handleContactSellerSubmit}>
                <Field
                  name="buyerEmail"
                  label="Your email"
                  type="email"
                  value={buyerEmail}
                  onChange={(_, value) => setBuyerEmail(value)}
                  placeholder="you@example.com"
                />

                <Field
                  name="message"
                  label="Message"
                  type="textarea"
                  value={message}
                  onChange={(_, value) => setMessage(value)}
                  placeholder={getContactMessagePlaceholder(itemCategoryKey)}
                />

                <button
                  type="submit"
                  className="hb-btn hb-btn--primary hb-btn--full"
                  disabled={sending}
                >
                  {sending ? "Sending…" : "Send message"}
                </button>

                {sentStatus && (
                  <div className={`hb-inline-status hb-inline-status--${sentStatus.type || "info"}`}>
                    {sentStatus.msg}
                  </div>
                )}
              </form>
            </div>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}

function HubMartAiSearch({
  query,
  setQuery,
  onSearch,
  state,
  compact = false,
}) {
  const isLoading = state?.status === "loading";
  const canSubmit = String(query || "").trim().length >= 3 && !isLoading;

  const submit = (event) => {
    event.preventDefault();
    if (canSubmit) onSearch();
  };

  return (
    <section
      className={`hb-ai-search ${compact ? "hb-ai-search--compact" : ""}`}
      aria-label="AI-assisted HubMart search"
    >
      <div className="hb-ai-search__intro">
        <span className="hb-ai-search__badge">✦ AI search</span>
        <div>
          <h2 className="hb-ai-search__title">Tell us what you need.</h2>
          <p className="hb-ai-search__text">
            Describe the item, budget, or condition. AI will choose the filters and
            search HubMart listings.
          </p>
        </div>
      </div>

      <form className="hb-ai-search__form" onSubmit={submit}>
        <div className="hb-ai-search__input-wrap">
          <FaSearch className="hb-ai-search__icon" aria-hidden="true" />
          <input
            className="hb-ai-search__input"
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder='Try: "Used iPhone under $300"'
            aria-label="Describe what you want to find in HubMart"
            autoComplete="off"
            disabled={isLoading}
          />
        </div>

        <button
          type="submit"
          className="hb-btn hb-btn--primary hb-ai-search__button"
          disabled={!canSubmit}
        >
          <span aria-hidden="true">✦</span>
          {isLoading ? "Searching…" : "Ask AI"}
        </button>
      </form>

      {state?.message && (
        <p
          className={`hb-ai-search__status hb-ai-search__status--${
            state.status || "info"
          }`}
          role={state.status === "error" ? "alert" : "status"}
        >
          {state.message}
        </p>
      )}
    </section>
  );
}

function MarketplaceHeader({
  filterCity,
  setFilterCity,
  activeCategory,
  setActiveCategory,
  search,
  setSearch,
  onSearchSubmit,
  showFilters,
  setShowFilters,
  isMobile,
  searchStatus,
  onMobileLandingPickCategory,
  onHeroPost,
  onHeroDonate,
  onHeroBulk,
  aiQuery,
  setAiQuery,
  onAiSearch,
  aiSearchState,
}) {
  const canSearch = !!activeCategory;

  if (isMobile) {
    return (
      <section className="hb-shell hb-shell--mobile-market">
        <div className="hb-shell__inner">
          <div {...heroBackdropProps("hb-mobile-market-hero")}>
            <div className="hb-eyebrow">GoOurHub Marketplace</div>

            <div className="hb-hero__cta">
              <button
                type="button"
                className="hb-btn hb-btn--primary hb-hero-btn"
                onClick={onHeroPost}
              >
                <FaPlus aria-hidden="true" />
                <span>Post</span>
              </button>
              <button
                type="button"
                className="hb-btn hb-btn--donate hb-hero-btn"
                onClick={onHeroDonate}
              >
                <FaGift aria-hidden="true" />
                <span>Donate</span>
                <span className="hb-hero-btn__tag">Free</span>
              </button>
              <div className="hb-bulk-cta-wrap hb-bulk-cta-wrap--mobile">
  <span className="hb-bulk-signin-ribbon">
    Sign in required
  </span>

  <button
    type="button"
    className="hb-btn hb-btn--bulk hb-hero-btn"
    onClick={onHeroBulk}
  >
    <FaPlus aria-hidden="true" />

    <span>
      Multiple Upload
    </span>

    <span className="hb-hero-btn__tag">
      Max 25
    </span>
  </button>
</div>
            </div>

            <h1 className="hb-hero__title">Pick a category and start browsing.</h1>
            <p className="hb-hero__text">
            Choose a category check out the listings or upload any.
            </p>
          </div>

          <HubMartAiSearch
            query={aiQuery}
            setQuery={setAiQuery}
            onSearch={onAiSearch}
            state={aiSearchState}
            compact
          />

          <div className="hb-command-card hb-command-card--mobile-compact">
            <div className="hb-mobile-control-row">
              <div className="hb-mobile-category-field">
                <label className="hb-form-label" htmlFor="hb-mobile-category">
                  Pick a category
                </label>
                <div className="hb-mobile-select-wrap">
                  <select
                    id="hb-mobile-category"
                    className="hb-input hb-mobile-category-select"
                    value={activeCategory || ""}
                    onChange={(event) => onMobileLandingPickCategory(event.target.value)}
                  >
                    <option value="">Choose category</option>
                    {CATEGORY_ORDER.map((cat) => (
                      <option key={cat} value={cat}>
                        {categoryLabel(cat)}
                      </option>
                    ))}
                  </select>
                  
                </div>
              </div>
            </div>

            <div className="hb-mobile-market-note">
              <span>Market</span>
              <div className="hb-mobile-market-pill">{CITY_OPTIONS[0]}</div>
            </div>

            <InlineStatus status={searchStatus} />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="hb-shell">
      <div className="hb-shell__inner">
        <div className="hb-hero">
          <div {...heroBackdropProps("hb-hero__copy")}>
            <div className="hb-eyebrow">GoOurHub Marketplace</div>

            <div className="hb-hero__cta">
              <button
                type="button"
                className="hb-btn hb-btn--primary hb-hero-btn"
                onClick={onHeroPost}
              >
                <FaPlus aria-hidden="true" />
                <span>Post a listing</span>
              </button>
              <button
                type="button"
                className="hb-btn hb-btn--donate hb-hero-btn"
                onClick={onHeroDonate}
              >
                <FaGift aria-hidden="true" />
                <span>Donate</span>
                <span className="hb-hero-btn__tag">Free</span>
              </button>
              <div className="hb-bulk-cta-wrap">
  <span className="hb-bulk-signin-ribbon">
    Sign in required
  </span>

  <button
    type="button"
    className="hb-btn hb-btn--bulk hb-hero-btn"
    onClick={onHeroBulk}
  >
    <FaPlus aria-hidden="true" />
    <span>Multiple Upload</span>

    <span className="hb-hero-btn__tag">
      Max 25
    </span>
  </button>
</div>
            </div>

            

            <h1 className="hb-hero__title">
              Buy and sell with your local community, all in one place.
            </h1>
            <p className="hb-hero__text">
              Browse local listings faster, search with less clutter, and post in a cleaner
              marketplace that actually feels ready to use.
            </p>
          </div>

          <div className="hb-hero__stats">
            {HERO_POINTS.map((point) => (
              <StatCard key={point.kicker} {...point} />
            ))}
          </div>
        </div>

        <HubMartAiSearch
          query={aiQuery}
          setQuery={setAiQuery}
          onSearch={onAiSearch}
          state={aiSearchState}
        />

        <div className="hb-command-card">
          <label className="hb-form-label" htmlFor="hb-city">
            Pick A Category
          </label>

          <div className="hb-category-row" role="tablist" aria-label="Browse categories">
            {CATEGORY_ORDER.map((cat) => {
              const isActive = normalizeCategoryName(activeCategory) === normalizeCategoryName(cat);

              return (
                <button
                  key={cat}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`hb-category-pill ${isActive ? "is-active" : ""}`}
                  onClick={() => setActiveCategory(cat)}
                >
                  <span aria-hidden="true">{categoryIcon(cat)}</span> {categoryLabel(cat)}
                </button>
              );
            })}
          </div>

          <div className="hb-search-grid">
            <div className="hb-form-group hb-form-group--wide">
              <input
                id="hb-search-desktop"
                className="hb-input"
                type="text"
                placeholder={
                  canSearch
                    ? `Search ${categoryLabel(activeCategory).toLowerCase()}…`
                    : "Pick a category first…"
                }
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                disabled={!canSearch}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && canSearch) onSearchSubmit();
                }}
              />
            </div>

            <div className="hb-form-group">
              <select
                id="hb-city"
                className="hb-input"
                value={filterCity}
                onChange={(event) => setFilterCity(event.target.value)}
              >
                {CITY_OPTIONS.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              className="hb-btn hb-btn--primary"
              disabled={!canSearch}
              onClick={onSearchSubmit}
            >
              Search listings
            </button>

            <button
              type="button"
              className={`hb-btn hb-btn--ghost ${showFilters ? "is-active" : ""}`}
              onClick={() => setShowFilters((prev) => !prev)}
            >
              {showFilters ? "Hide filters" : "Show filters"}
            </button>
          </div>

          <InlineStatus status={searchStatus} />
        </div>
      </div>
    </section>
  );
}

function FiltersSidebar({
  show,
  activeCategory,
  priceMin,
  priceMax,
  setPriceMin,
  setPriceMax,
  filterCondition,
  setFilterCondition,
  onPriceApply,
  onPriceClear,
}) {
  if (!show || !activeCategory) return null;

  const categoryKey = normalizeCategoryName(activeCategory);
  const showPriceFilter = ![CATEGORY_FREE, "job", VIRTUAL_CATEGORY_THREE].includes(categoryKey);
  const showConditionFilter = (CATEGORY_FIELDS[categoryKey] || []).some(
    (field) => field.name === "condition"
  );

  return (
    <aside className="hb-sidebar" aria-label="Filters">
      <div className="hb-sidebar__card">
        <div className="hb-sidebar__header">
          <div>
            <p className="hb-sidebar__eyebrow">Refine results</p>
            <h3 className="hb-sidebar__title">Filters</h3>
          </div>
          <span className="hb-badge hb-badge--soft">{categoryLabel(activeCategory)}</span>
        </div>

        {showPriceFilter && (
          <div className="hb-sidebar__section">
            <label className="hb-form-label">Price range</label>
            <div className="hb-price-row">
              <input
                className="hb-input"
                type="number"
                min="0"
                placeholder="Min"
                value={priceMin}
                onChange={(event) => setPriceMin(event.target.value)}
              />
              <span className="hb-price-row__divider"> </span>
              <input
                className="hb-input"
                type="number"
                min="0"
                placeholder="Max"
                value={priceMax}
                onChange={(event) => setPriceMax(event.target.value)}
              />
            </div>

            <div className="hb-sidebar__actions">
              <button
                type="button"
                className="hb-btn hb-btn--primary hb-btn--full"
                onClick={onPriceApply}
              >
                Apply filters
              </button>
              <button
                type="button"
                className="hb-btn hb-btn--ghost hb-btn--full"
                onClick={onPriceClear}
              >
                Reset
              </button>
            </div>
          </div>
        )}

        {showConditionFilter && (
          <div className="hb-sidebar__section">
            <label className="hb-form-label">Condition</label>
            <select
              className="hb-input"
              value={filterCondition}
              onChange={(event) => setFilterCondition(event.target.value)}
            >
              <option value="">All conditions</option>
              {CONDITION_OPTIONS.map((condition) => (
                <option key={condition} value={condition}>
                  {condition}
                </option>
              ))}
            </select>
          </div>
        )}

        {!showPriceFilter && !showConditionFilter && (
          <div className="hb-sidebar__section">
            <p>No extra filters are needed for this category.</p>
          </div>
        )}

        <div className="hb-sidebar__footnote">
          Keep filters tight so buyers don’t get lost. That is half the battle right there.
        </div>
      </div>
    </aside>
  );
}

function MobileFilterSheet({
  open,
  onClose,
  activeCategory,
  priceMin,
  priceMax,
  setPriceMin,
  setPriceMax,
  filterCondition,
  setFilterCondition,
  onApply,
  onClearAndReset,
}) {
  if (!open || !activeCategory) return null;

  const categoryKey = normalizeCategoryName(activeCategory);
  const showPriceFilter = ![CATEGORY_FREE, "job", VIRTUAL_CATEGORY_THREE].includes(categoryKey);
  const showConditionFilter = (CATEGORY_FIELDS[categoryKey] || []).some(
    (field) => field.name === "condition"
  );

  const clearLocal = () => {
    setPriceMin("");
    setPriceMax("");
    setFilterCondition("");
  };

  return (
    <div className="hb-mobile-sheet-backdrop" onClick={onClose}>
      <aside
        className="hb-mobile-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Filters"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="hb-mobile-sheet__header">
          <button
            type="button"
            className="hb-mobile-sheet__iconbtn"
            onClick={onClose}
            aria-label="Close filters"
          >
            <FaTimes />
          </button>
          <h3>Filters</h3>
          <button type="button" className="hb-mobile-sheet__clear" onClick={clearLocal}>
            Clear
          </button>
        </div>

        <div className="hb-mobile-sheet__body">
          {showPriceFilter && (
            <div className="hb-mobile-sheet__group">
              <label className="hb-mobile-sheet__label">Price range</label>
              <div className="hb-mobile-price-row">
                <div className="hb-mobile-price-inputwrap">
                  <input
                    className="hb-input hb-input--compact"
                    type="number"
                    min="0"
                    placeholder="Min"
                    value={priceMin}
                    onChange={(event) => setPriceMin(event.target.value)}
                  />
                  {priceMin !== "" && (
                    <button
                      type="button"
                      className="hb-mobile-price-clear"
                      onClick={() => setPriceMin("")}
                    >
                      ×
                    </button>
                  )}
                </div>

                <span className="hb-mobile-price-sep">to</span>

                <div className="hb-mobile-price-inputwrap">
                  <input
                    className="hb-input hb-input--compact"
                    type="number"
                    min="0"
                    placeholder="Max"
                    value={priceMax}
                    onChange={(event) => setPriceMax(event.target.value)}
                  />
                  {priceMax !== "" && (
                    <button
                      type="button"
                      className="hb-mobile-price-clear"
                      onClick={() => setPriceMax("")}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {showConditionFilter && (
            <div className="hb-mobile-sheet__group">
              <label className="hb-mobile-sheet__label">Condition</label>
              <div className="hb-mobile-condition-list">
                {CONDITION_OPTIONS.map((condition) => {
                  const selected = String(filterCondition || "") === condition;

                  return (
                    <button
                      key={condition}
                      type="button"
                      className={`hb-mobile-condition-row ${selected ? "is-active" : ""}`}
                      onClick={() => setFilterCondition(selected ? "" : condition)}
                    >
                      <span className="hb-mobile-condition-row__box">{selected ? "✓" : ""}</span>
                      <span className="hb-mobile-condition-row__copy">{condition}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {!showPriceFilter && !showConditionFilter && (
            <div className="hb-mobile-sheet__group">
              <p>No extra filters are needed for this category.</p>
            </div>
          )}
        </div>

        <div className="hb-mobile-sheet__footer">
          <button
            type="button"
            className="hb-btn hb-btn--primary hb-btn--full"
            onClick={async () => {
              await onApply();
              onClose();
            }}
          >
            Show results
          </button>

          <button
            type="button"
            className="hb-btn hb-btn--ghost hb-btn--full"
            onClick={async () => {
              await onClearAndReset();
              onClose();
            }}
          >
            Reset all
          </button>
        </div>
      </aside>
    </div>
  );
}

function MobileSortSheet({ open, onClose, sortBy, setSortBy, onAfterChange, activeCategory }) {
  if (!open) return null;

  return (
    <div className="hb-mobile-sheet-backdrop" onClick={onClose}>
      <aside
        className="hb-mobile-sheet hb-mobile-sheet--small"
        role="dialog"
        aria-modal="true"
        aria-label="Sort"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="hb-mobile-sheet__header">
          <button
            type="button"
            className="hb-mobile-sheet__iconbtn"
            onClick={onClose}
            aria-label="Close sort"
          >
            <FaTimes />
          </button>
          <h3>Sort</h3>
          <button
            type="button"
            className="hb-mobile-sheet__clear"
            onClick={() => {
              setSortBy("newest");
              onAfterChange("newest");
              onClose();
            }}
          >
            Clear
          </button>
        </div>

        <div className="hb-mobile-sheet__body">
          {getSortOptionsForCategory(activeCategory, true).map((option) => {
            const active = sortBy === option.value;

            return (
              <button
                key={option.value}
                type="button"
                className={`hb-mobile-sort-option ${active ? "is-active" : ""}`}
                onClick={() => {
                  setSortBy(option.value);
                  onAfterChange(option.value);
                  onClose();
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </aside>
    </div>
  );
}

function PaginationControls({ pagination, currentCount, isLoading, onPageChange }) {
  const totalCount = Number(pagination?.totalCount || 0);
  const totalPages = Math.max(1, Number(pagination?.totalPages || 1));
  const currentPage = Math.min(Math.max(1, Number(pagination?.page || 1)), totalPages);
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  if (!totalCount && totalPages <= 1 && currentPage <= 1) return null;

  const visiblePages = getVisiblePaginationPages(currentPage, totalPages);

  return (
    <div className="hb-pagination">
      <div className="hb-pagination__summary">
        {totalCount ? (
          <>
            Total {totalCount} item{totalCount === 1 ? "" : "s"} · Showing {currentCount} · Page{" "}
            {currentPage} of {totalPages}
          </>
        ) : (
          <>
            Showing {currentCount} · Page {currentPage} of {totalPages}
          </>
        )}
      </div>

      {(totalPages > 1 || currentPage > 1) && (
        <div className="hb-pagination__controls" aria-label="Pagination">
          <button
            type="button"
            className="hb-btn hb-btn--ghost hb-pagination__btn"
            disabled={isLoading || !canGoPrevious}
            onClick={() => onPageChange(currentPage - 1)}
          >
            Previous
          </button>

          {visiblePages.map((pageItem, index) => {
            if (typeof pageItem === "string") {
              return (
                <span key={`${pageItem}-${index}`} className="hb-pagination__ellipsis">
                  …
                </span>
              );
            }

            const isActive = pageItem === currentPage;

            return (
              <button
                key={pageItem}
                type="button"
                className={`hb-pagination__page ${isActive ? "is-active" : ""}`}
                disabled={isLoading || isActive}
                aria-current={isActive ? "page" : undefined}
                onClick={() => onPageChange(pageItem)}
              >
                {pageItem}
              </button>
            );
          })}

          <button
            type="button"
            className="hb-btn hb-btn--ghost hb-pagination__btn"
            disabled={isLoading || !canGoNext}
            onClick={() => onPageChange(currentPage + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

/* =========================
   MAIN COMPONENT
   ========================= */

export default function HubMart() {
    const user = useSession();

  const isSignedIn = Boolean(
    user?.email &&
    user?.token
  );
  const [hash, setHash] = useState(safeHash());
  const [allItems, setAllItems] = useState([]);
  const [allBase, setAllBase] = useState([]);
  const [isBootLoading, setIsBootLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [filterCity, setFilterCity] = useState(CITY_OPTIONS[0] || "");
  const [filterCondition, setFilterCondition] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  const [showFilters, setShowFilters] = useState(
    typeof window !== "undefined" ? window.innerWidth > 768 : true
  );
  const [showPostForm, setShowPostForm] = useState(false);
  const [showBulkForm, setShowBulkForm] =
  useState(false);
  const [bulkItems, setBulkItems] =
  useState(() => [
    createBulkItem(),
  ]);

const [isBulkUploading, setIsBulkUploading] =
  useState(false);

const [bulkProgress, setBulkProgress] =
  useState({
    current: 0,
    total: 0,
  });

const [bulkErrors, setBulkErrors] =
  useState([]);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [mobileSortOpen, setMobileSortOpen] = useState(false);
  const [mobileStage, setMobileStage] = useState("browse");
  const [draft, setDraft] = useState({ ...defaultListingTemplate });
  const [images, setImages] = useState([]);
  const [notice, setNotice] = useState(null);
  const [userLocation, setUserLocation] = useState({ lat: null, lon: null });
  const [isPosting, setIsPosting] = useState(false);
  const [searchStatus, setSearchStatus] = useState(null);
  const [aiQuery, setAiQuery] = useState("");
  const [aiSearchState, setAiSearchState] = useState({
    status: "idle",
    message: "",
  });
  const [pagination, setPagination] = useState(() => createEmptyPagination());

  const resultsRef = useRef(null);
  const paginationRef = useRef(pagination);
  const aiRequestRef = useRef(null);
  const skipNextCategoryLoadRef = useRef(false);

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  const detailId = useMemo(() => {
    const match = hash.match(/^#\/item\/(.+)$/);
    return match ? decodeURIComponent(match[1]) : null;
  }, [hash]);

  const POSTABLE_CATEGORY_OPTIONS = useMemo(
    () =>
      CATEGORY_ORDER.filter((cat) => !isVirtualCategory(cat) && getCatIdByName(cat) != null).map(
        (cat) => ({
          value: cat,
          label: categoryLabel(cat),
        })
      ),
    []
  );

  const currentRequestKey = useMemo(
    () =>
      makeRequestKey({
        categoryKey: activeCategory,
        query: search,
        priceMin,
        priceMax,
        condition: filterCondition,
      }),
     [activeCategory, search, priceMin, priceMax, filterCondition]
  );

  const scrollResultsIntoView = () => {
    window.setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
  };

  const sendResultsToUser = (message, type = "success") => {
    setSearchStatus({ type, text: message });

    if (isMobile) setMobileStage("results");
    else scrollResultsIntoView();
  };

  const loadVirtualCategoryPage = async (pageNumber, options = {}) => {
    const key = normalizeCategoryName(activeCategory);
    const query = options.queryOverride !== undefined ? options.queryOverride : search.trim();
    const requestKey =
      options.requestKey ||
      makeRequestKey({
        categoryKey: key,
        query,
        priceMin: "",
        priceMax: "",
         
      });

    let base = allBase;

    if (!base.length || options.reset) {
      base = await fetchEverything();
      setAllBase(base);
    }

    let working = query
      ? base.filter((item) => listingSearchText(item).includes(query.toLowerCase()))
      : base;

    if (key === VIRTUAL_CATEGORY_THREE) {
      working = working.filter((item) => Number(item.price) === 3);
    }

    working = dedupeListings(attachCachedImages(working));

    const totalCount = working.length;
    const totalPages = getTotalPages(totalCount);
    const safePage = Math.min(Math.max(Number(pageNumber) || 1, 1), totalPages);
    const start = (safePage - 1) * PAGE_LIMIT;
    const pageItems = working.slice(start, start + PAGE_LIMIT);

    setAllItems(pageItems);
    setPagination({
      page: safePage,
      totalCount,
      totalPages,
      requestKey,
      pages: { [safePage]: pageItems },
      cursors: { 1: "" },
    });

    sendResultsToUser(
      totalCount
        ? `Loaded ${pageItems.length} of ${totalCount} result${totalCount === 1 ? "" : "s"}.`
        : "No matches yet. Try a broader word or clear the search.",
      totalCount ? "success" : "empty"
    );
  };

  const loadPageForCurrentRequest = async (targetPage = 1, options = {}) => {
    const key = normalizeCategoryName(
      options.categoryKeyOverride !== undefined
        ? options.categoryKeyOverride
        : activeCategory
    );

    if (!key) return { ok: false, totalCount: 0 };

    const query =
      options.queryOverride !== undefined ? options.queryOverride : String(search || "").trim();

    const requestPriceMin =
      options.priceMinOverride !== undefined ? options.priceMinOverride : priceMin;

    const requestPriceMax =
      options.priceMaxOverride !== undefined ? options.priceMaxOverride : priceMax;

    const requestCondition =
      options.conditionOverride !== undefined
        ? options.conditionOverride
        : filterCondition;

    const requestKey =
      options.requestKey ||
      makeRequestKey({
        categoryKey: key,
        query,
        priceMin: requestPriceMin,
        priceMax: requestPriceMax,
        condition: requestCondition,
      });

    setIsBootLoading(true);

    try {
      if (isVirtualCategory(key)) {
        await loadVirtualCategoryPage(targetPage, {
          ...options,
          queryOverride: query,
          requestKey,
        });
        return { ok: true, totalCount: 0 };
      }

      const catId = getCatIdByName(key);

      if (catId == null) {
        setAllItems([]);
        setPagination(createEmptyPagination({ requestKey }));
        setSearchStatus({ type: "error", text: "That category is not mapped to the API." });
        return { ok: false, totalCount: 0 };
      }

      const current = paginationRef.current;
      const sameRequest = current.requestKey === requestKey && !options.reset;

      if (sameRequest && current.pages[targetPage]) {
        const cachedItems = current.pages[targetPage];

        setAllItems(cachedItems);
        setPagination((prev) => ({
          ...prev,
          page: targetPage,
        }));

        sendResultsToUser(
          `Page ${targetPage} loaded from cache. Total ${current.totalCount} item${
            current.totalCount === 1 ? "" : "s"
          }.`,
          "success"
        );
        return {
          ok: true,
          totalCount: Number(current.totalCount || cachedItems.length || 0),
        };
      }

      const pages = sameRequest ? { ...current.pages } : {};
      const cursors = sameRequest ? { ...current.cursors } : { 1: "" };

      let totalCount = sameRequest ? Number(current.totalCount || 0) : 0;
      let totalPages = sameRequest ? Number(current.totalPages || 1) : 1;

      for (let page = 1; page <= targetPage; page += 1) {
        if (pages[page] && cursors[page + 1] !== undefined) continue;

        const cursor = page === 1 ? "" : cursors[page];

        if (page > 1 && !cursor) {
          throw new Error("Pagination id is missing for the next page.");
        }

const pageData = await fetchListingsPage({
  categoryId: catId,
  sentence: query,
  minPrice: requestPriceMin,
  maxPrice: requestPriceMax,
  condition: requestCondition,
  cursor,
});

        const cleanItems = dedupeListings(attachCachedImages(pageData.items));

        pages[page] = cleanItems;

        const apiTotalCount =
          pageData.totalCount === null || pageData.totalCount === undefined
            ? null
            : Number(pageData.totalCount);

        if (apiTotalCount !== null && Number.isFinite(apiTotalCount) && apiTotalCount >= 0) {
          if (page === 1 || !totalCount || apiTotalCount >= totalCount) {
            totalCount = apiTotalCount;
            totalPages = getTotalPages(apiTotalCount);
          }
        } else if (!totalCount && page === 1) {
          totalCount = cleanItems.length;
          totalPages = getTotalPages(totalCount);
        }

        const apiTotalPages =
          pageData.totalPages === null || pageData.totalPages === undefined
            ? null
            : Number(pageData.totalPages);

        if (
          apiTotalPages !== null &&
          Number.isFinite(apiTotalPages) &&
          apiTotalPages > 0 &&
          (page === 1 || apiTotalPages >= totalPages)
        ) {
          totalPages = apiTotalPages;
        }

        if (page < totalPages && pageData.nextCursor) {
          cursors[page + 1] = pageData.nextCursor;
        } else {
          cursors[page + 1] = "";
        }
      }

      const pageItems = pages[targetPage] || [];

      setAllItems(pageItems);
      setPagination({
        page: targetPage,
        totalCount,
        totalPages,
        requestKey,
        pages,
        cursors,
      });

      sendResultsToUser(
        totalCount
          ? `Loaded page ${targetPage}. Showing ${pageItems.length} of ${totalCount} item${
              totalCount === 1 ? "" : "s"
            }.`
          : "No matches yet. Try a broader word or clear the search.",
        totalCount ? "success" : "empty"
      );
      return { ok: true, totalCount };
    } catch (err) {
      setAllItems([]);
      setNotice({ type: "error", text: err?.message || "Could not load listings." });
      setSearchStatus({ type: "error", text: err?.message || "Could not load listings." });
      return { ok: false, totalCount: 0 };
    } finally {
      setIsBootLoading(false);
    }
  };

  const handlePageChange = async (nextPage) => {
    const page = Number(nextPage);
    const current = paginationRef.current;
    const maxPage = Math.max(1, Number(current.totalPages || pagination.totalPages || 1));

    if (!page || page < 1 || page > maxPage) return;

    if (current.requestKey === currentRequestKey && current.pages[page]) {
      setAllItems(current.pages[page]);
      setPagination((prev) => ({
        ...prev,
        page,
        totalPages: maxPage,
      }));

      setSearchStatus({
        type: "success",
        text: `Page ${page} loaded from cache.`,
      });

      scrollResultsIntoView();
      return;
    }

    await loadPageForCurrentRequest(page);
  };

  useEffect(() => {
    const onHash = () => setHash(safeHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(
    () => () => {
      aiRequestRef.current?.abort();
    },
    []
  );

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(null), 5000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (pos) => setUserLocation({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      () => {}
    );
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Keep the floating Post button just below the real rendered header.
  // In the Capacitor iOS WebView, env(safe-area-inset-top) is unreliable
  // and the header renders taller than in Safari, so any hardcoded top
  // gets clipped. Measuring the header works in every environment, and
  // the result is applied as an inline style so no stylesheet rule can
  // override it.
  useEffect(() => {
    const syncPostButtonTop = () => {
      const nodes = document.querySelectorAll(
        ".headerSection, .headerSection *, header"
      );

      let bottom = 0;

      nodes.forEach((el) => {
        const rect = el.getBoundingClientRect();

        // Only elements pinned to the top of the viewport count as
        // "the header"; the caps guard against absurd measurements.
        if (rect.top <= 8 && rect.bottom < 260 && rect.bottom > bottom) {
          bottom = rect.bottom;
        }
      });

      if (bottom > 40) {
        const top = Math.round(bottom) + 10;

        // Set the shared variable for any stylesheet that uses it…
        document.documentElement.style.setProperty("--hb-post-top", `${top}px`);

        // …and style the button directly. The floating button's JSX
        // lives outside this file, and an inline style beats every
        // stylesheet rule regardless of cascade order.
        document.querySelectorAll(".hb-post-floating").forEach((btn) => {
          btn.style.top = `${top}px`;
        });
      }
    };

    syncPostButtonTop();

    // Capacitor applies native insets after first paint, so measure a
    // few more times while layout settles.
    const settleTimers = [200, 600, 1500].map((delay) =>
      window.setTimeout(syncPostButtonTop, delay)
    );

    window.addEventListener("resize", syncPostButtonTop);
    window.addEventListener("orientationchange", syncPostButtonTop);

    return () => {
      settleTimers.forEach((timer) => window.clearTimeout(timer));
      window.removeEventListener("resize", syncPostButtonTop);
      window.removeEventListener("orientationchange", syncPostButtonTop);
    };
  }, []);

  useEffect(() => {
    if (isMobile) {
      setShowFilters(false);
    } else {
      setMobileFilterOpen(false);
      setMobileSortOpen(false);
      setShowFilters(true);
      setMobileStage("browse");
    }
  }, [isMobile]);

  useEffect(() => {
    if (!showPostForm) return undefined;

    const onKey = (event) => event.key === "Escape" && setShowPostForm(false);
    window.addEventListener("keydown", onKey);

    return () => window.removeEventListener("keydown", onKey);
  }, [showPostForm]);

  useEffect(() => {
    if (!activeCategory) return;
    if (skipNextCategoryLoadRef.current) {
      skipNextCategoryLoadRef.current = false;
      return;
    }

    loadPageForCurrentRequest(1, {
      reset: true,
      requestKey: makeRequestKey({
  categoryKey: activeCategory,
  query: "",
      priceMin: "",
      priceMax: "",
      condition: "",
    }),
    queryOverride: "",
    priceMinOverride: "",
    priceMaxOverride: "",
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [activeCategory]);

  const previewImageUrls = useMemo(() => images.map((file) => URL.createObjectURL(file)), [images]);

  useEffect(() => {
    return () => previewImageUrls.forEach((url) => URL.revokeObjectURL(url));
  }, [previewImageUrls]);

  const handleDraftChange = (key, value) => {
    if (key === "category") {
      const nextCategory = normalizeCategoryName(value);

      setDraft((prev) => {
        const previousCategory = normalizeCategoryName(prev.category);
        let nextPrice = prev.price;

        if (nextCategory === CATEGORY_FREE) {
          nextPrice = "0";
        } else if (nextCategory === "job") {
          nextPrice = "";
        } else if (
          [CATEGORY_FREE, "job"].includes(previousCategory) &&
          (prev.price === "" || Number(prev.price) === 0)
        ) {
          nextPrice = "";
        }

        return {
          ...prev,
          category: nextCategory,
          price: nextPrice,
        };
      });

      return;
    }

    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleContactChange = (field, value) => {
    setDraft((prev) => ({ ...prev, contact: { ...(prev.contact || {}), [field]: value } }));
  };

const handleImagesSelect = (event) => {
  const selectedFiles = Array.from(event.target.files || []).filter((file) =>
    file.type.startsWith("image/")
  );

  setImages((prev) => {
    const combined = [...prev, ...selectedFiles];

    const seen = new Set();

    const uniqueFiles = combined.filter((file) => {
      const key = `${file.name}-${file.size}-${file.lastModified}`;

      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });

    return uniqueFiles.slice(0, MAX_IMAGES);
  });

  // Allows selecting another photo afterward,
  // including the same filename if needed.
  event.target.value = "";
};

  const removeImageAt = (index) => {
    setImages((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleAiSearch = async () => {
    const requestQuery = String(aiQuery || "").trim();

    if (requestQuery.length < 3) {
      setAiSearchState({
        status: "error",
        message: "Describe what you need using at least 3 characters.",
      });
      return;
    }

    aiRequestRef.current?.abort();
    const controller = new AbortController();
    aiRequestRef.current = controller;
    const timeoutId = window.setTimeout(() => controller.abort(), 40000);

    setAiSearchState({
      status: "loading",
      message: "Understanding your request and choosing HubMart filters…",
    });

    try {
      const response = await fetch(`${BUSINESS_API_BASE}/api/ai/hub-search`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: requestQuery,
          city: "San Antonio",
          scope: "hubmart",
        }),
        signal: controller.signal,
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload?.success === false) {
        throw new Error(
          payload?.message || `AI search is unavailable (${response.status}).`
        );
      }

      const filters = payload?.filters || {};
      const aiCategoryKey = normalizeCategoryName(filters?.hubmartCategory);
      const categoryKey = shouldUseTraditionalWear(requestQuery)
        ? "traditional"
        : aiCategoryKey;
      const categoryId = getCatIdByName(categoryKey);

      if (!categoryKey || categoryId == null || isVirtualCategory(categoryKey)) {
        throw new Error(
          "AI could not determine the HubMart category. Try mentioning the item type."
        );
      }

      const nextPriceMin =
        filters?.hubmartMinPrice === null ||
        filters?.hubmartMinPrice === undefined
          ? ""
          : String(filters.hubmartMinPrice);
      const nextPriceMax =
        filters?.hubmartMaxPrice === null ||
        filters?.hubmartMaxPrice === undefined
          ? ""
          : String(filters.hubmartMaxPrice);
      const nextCondition =
        CONDITION_OPTIONS.find(
          (condition) =>
            condition.toLowerCase() ===
            String(filters?.hubmartCondition || "").toLowerCase()
        ) || "";
      const ignoredKeywordTokens = new Set([
        "find",
        "free",
        "hubmart",
        "item",
        "items",
        "listing",
        "listings",
        "marketplace",
        "me",
        "please",
        "show",
        "stuff",
        categoryKey.toLowerCase(),
        ...(AI_CATEGORY_GENERIC_TOKENS[categoryKey] || []),
        ...nextCondition.toLowerCase().split(/\s+/).filter(Boolean),
      ]);
      const keywordQuery = Array.isArray(filters?.keywords)
        ? filters.keywords
            .slice(0, 6)
            .flatMap((item) => String(item || "").trim().split(/\s+/))
            .map((token) => token.replace(/^[^a-z0-9]+|[^a-z0-9]+$/gi, ""))
            .filter(
              (token) =>
                token && !ignoredKeywordTokens.has(token.toLowerCase())
            )
            .join(" ")
        : "";
      // When AI has already chosen the category/price/condition and no useful
      // keywords remain, an empty sentence intentionally loads that category.
      // Falling back to the visitor's conversational phrase (for example,
      // "free stuff") over-constrains HubMart's literal search endpoint.
      const nextSearch = keywordQuery;
      const nextSort = SORT_OPTIONS.some(
        (option) => option.value === filters?.hubmartSort
      )
        ? filters.hubmartSort
        : "newest";

      if (normalizeCategoryName(activeCategory) !== categoryKey) {
        skipNextCategoryLoadRef.current = true;
      }

      setActiveCategory(categoryKey);
      setSearch(nextSearch);
      setPriceMin(nextPriceMin);
      setPriceMax(nextPriceMax);
      setFilterCondition(nextCondition);
      setSortBy(nextSort);
      setShowFilters(Boolean(nextPriceMin || nextPriceMax || nextCondition));
      setAllItems([]);
      setPagination(createEmptyPagination());
      setSearchStatus({
        type: "loading",
        text: `AI selected ${categoryLabel(categoryKey)}. Searching listings…`,
      });

      if (isMobile) setMobileStage("results");

      const individualKeywords = nextSearch.split(/\s+/).filter(Boolean);
      const searchCandidates = Array.from(
        new Set([nextSearch, ...individualKeywords, ""])
      );
      let usedSearch = nextSearch;
      let searchResult = { ok: false, totalCount: 0 };

      for (const candidate of searchCandidates) {
        usedSearch = candidate;
        searchResult = await loadPageForCurrentRequest(1, {
          reset: true,
          categoryKeyOverride: categoryKey,
          queryOverride: candidate,
          priceMinOverride: nextPriceMin,
          priceMaxOverride: nextPriceMax,
          conditionOverride: nextCondition,
          requestKey: makeRequestKey({
            categoryKey,
            query: candidate,
            priceMin: nextPriceMin,
            priceMax: nextPriceMax,
            condition: nextCondition,
          }),
        });

        if (!searchResult.ok) {
          throw new Error(
            "AI understood the request, but HubMart listings could not be loaded."
          );
        }

        if (searchResult.totalCount > 0 || candidate === "") break;
      }

      setSearch(usedSearch);

      const foundListings = searchResult.totalCount > 0;
      const broadenedSearch = usedSearch !== nextSearch;
      const fallbackMessage = foundListings
        ? `AI selected ${categoryLabel(categoryKey)} and applied your filters.`
        : `AI selected ${categoryLabel(categoryKey)}, but no listings are currently available.`;
      const broadenedMessage = broadenedSearch
        ? usedSearch
          ? ` Broadened the keyword search to “${usedSearch}”.`
          : ` Showing the closest ${categoryLabel(categoryKey).toLowerCase()} listings.`
        : "";

      setAiSearchState({
        status: foundListings ? "success" : "empty",
        message: `${payload?.message || fallbackMessage}${broadenedMessage}`,
      });
    } catch (error) {
      const message =
        error?.name === "AbortError"
          ? "AI search took too long. Please try again."
          : error?.message || "AI search is temporarily unavailable.";

      setAiSearchState({ status: "error", message });
      setSearchStatus({ type: "error", text: message });
    } finally {
      window.clearTimeout(timeoutId);
      if (aiRequestRef.current === controller) {
        aiRequestRef.current = null;
      }
    }
  };

  const handleCategoryPick = (cat) => {
    const key = normalizeCategoryName(cat);

    setSearch("");
    setPriceMin("");
    setPriceMax("");
    setFilterCondition("");
    setSortBy("newest");
    setAllItems([]);
    setPagination(createEmptyPagination());
    setActiveCategory(key);
    setShowPostForm(false);
    setAiSearchState({ status: "idle", message: "" });

    if (!key) {
      setSearchStatus(null);
      if (isMobile) setMobileStage("browse");
      return;
    }

    setSearchStatus({
      type: "loading",
      text: `Loading ${categoryLabel(key).toLowerCase()} listings…`,
    });

    if (isMobile) setMobileStage("results");
  };

  const onSearchSubmit = async () => {
    if (!activeCategory) {
      setNotice({ type: "error", text: "Pick a category before searching." });
      setSearchStatus({ type: "error", text: "Choose a category first, then search." });
      return;
    }

    setPagination(createEmptyPagination());

    await loadPageForCurrentRequest(1, {
      reset: true,
    });
  };

  const handlePriceApply = async () => {
    if (!activeCategory) return;

    setPagination(createEmptyPagination());

    await loadPageForCurrentRequest(1, {
      reset: true,
    });
  };

  const handlePriceClear = async () => {
    setPriceMin("");
    setPriceMax("");
    setFilterCondition("");
    setPagination(createEmptyPagination());

    if (!activeCategory) return;

    await loadPageForCurrentRequest(1, {
      reset: true,
      priceMinOverride: "",
      priceMaxOverride: "",
     
    });
  };

  const handleMobileSortChange = (value) => {
    setSortBy(value);
    sendResultsToUser(`Sorted by ${getSortLabel(value).toLowerCase()}.`, "success");
  };

  const filtered = useMemo(() => {
    let arr = dedupeListings(allItems);
    const key = normalizeCategoryName(activeCategory);
// Hide closed / expired jobs from HubMart results.
if (key === "job") {
  const now = Date.now();

  arr = arr.filter((item) => {
    const status = String(item?.status || "")
      .trim()
      .toLowerCase();

    // Explicitly closed jobs should never display.
    if (
      status === "expired" ||
      status === "inactive" ||
      status === "closed"
    ) {
      return false;
    }

    // No closing date = keep showing it.
    // reviewAfter will be used for admin re-verification later.
    if (!item?.closingDate) {
      return true;
    }

    const closingTime = new Date(item.closingDate).getTime();

    // If somebody entered an invalid date, do not
    // accidentally remove the job.
    if (Number.isNaN(closingTime)) {
      return true;
    }

    return closingTime >= now;
  });
}
    if (key === CATEGORY_FREE) {
      arr = arr.filter((item) => {
        const itemCategory = normalizeCategoryName(item?.category ?? item?.categoryId);
        return itemCategory === CATEGORY_FREE || Number(item?.categoryId) === 0;
      });
    }

    if (key === VIRTUAL_CATEGORY_THREE) {
      arr = arr.filter((item) => Number(item.price) === 3);
    }

    if (filterCity) {
      const cityFull = filterCity.toLowerCase();
      const cityOnly = cityFull.split(",")[0].trim();

      arr = arr.filter((item) => {
        const text = listingLocationText(item).toLowerCase();

        if (!text) return true;

        return text.includes(cityFull) || text.includes(cityOnly);
      });
    }

    if (filterCondition) {
      arr = arr.filter(
        (item) => String(item.condition || "").toLowerCase() === filterCondition.toLowerCase()
      );
    }

    return sortListings(arr, sortBy, userLocation);
  }, [allItems, activeCategory, filterCity, filterCondition, sortBy, userLocation]);

  const previewListing = useMemo(() => {
    const categoryKey = normalizeCategoryName(draft.category || activeCategory || "");
    const formConfig = getCategoryFormConfig(categoryKey);

    return {
      ...draft,
      id: "preview",
      title: draft.title || formConfig.titlePlaceholder || "Your listing preview",
      price:
        categoryKey === "job"
          ? undefined
          : draft.price === ""
          ? 0
          : Number(draft.price),
      description:
        draft.description ||
        formConfig.descriptionPlaceholder ||
        "Add a clear description so people understand the listing.",
      seller: "You",
      location: draft.location || filterCity || "",
      images: previewImageUrls,
      image: "",
      category: categoryKey,
      condition: draft.condition || "",
      contact: draft.contact,
      lat: userLocation.lat,
      lon: userLocation.lon,
    };
  }, [draft, previewImageUrls, activeCategory, filterCity, userLocation]);

  const openListingInSameTab = (item) => {
    const id = String(stableId(item));

    try {
      sessionStorage.setItem(`hb:item:${id}`, JSON.stringify(item));
    } catch {
      // ignore
    }

    window.location.hash = `#/item/${encodeURIComponent(id)}`;
  };
  const handleBulkItemChange = (
  itemId,
  field,
  value
) => {
  setBulkItems((current) =>
    current.map((item) => {
      if (item.id !== itemId) {
        return item;
      }

      const next = {
        ...item,
        [field]: value,
      };

      // Free items always have price 0.
      if (
        field === "category" &&
        value === "free"
      ) {
        next.price = "0";
      }

      return next;
    })
  );
};

const addBulkItem = () => {
  setBulkItems((current) => {
    if (
      current.length >=
      MAX_BULK_ITEMS
    ) {
      return current;
    }

    return [
      ...current,
      createBulkItem(),
    ];
  });
};

const removeBulkItem = (itemId) => {
  setBulkItems((current) => {
    if (current.length === 1) {
      return current;
    }

    return current.filter(
      (item) => item.id !== itemId
    );
  });
};

const handleBulkImagesSelect = (
  itemId,
  event
) => {
  const selected =
    Array.from(
      event.target.files || []
    ).filter((file) =>
      file.type.startsWith("image/")
    );

  setBulkItems((current) =>
    current.map((item) => {
      if (item.id !== itemId) {
        return item;
      }

      const combined = [
        ...(item.images || []),
        ...selected,
      ];

      const seen = new Set();

      const unique =
        combined.filter((file) => {
          const key =
            `${file.name}-${file.size}-${file.lastModified}`;

          if (seen.has(key)) {
            return false;
          }

          seen.add(key);
          return true;
        });

      return {
        ...item,
        images:
          unique.slice(
            0,
            MAX_IMAGES
          ),
      };
    })
  );

  event.target.value = "";
};

const removeBulkImage = (
  itemId,
  imageIndex
) => {
  setBulkItems((current) =>
    current.map((item) =>
      item.id === itemId
        ? {
            ...item,
            images:
              item.images.filter(
                (_, index) =>
                  index !==
                  imageIndex
              ),
          }
        : item
    )
  );
};
const validateBulkItems = () => {
  const errors = [];

  if (
    bulkItems.length < 1 ||
    bulkItems.length > MAX_BULK_ITEMS
  ) {
    errors.push(
      `Bulk upload must contain between 1 and ${MAX_BULK_ITEMS} items.`
    );
  }

  bulkItems.forEach((item, index) => {
    const number = index + 1;
    const category =
      normalizeCategoryName(
        item.category
      );

    if (!item.title?.trim()) {
      errors.push(
        `Item ${number}: title is required.`
      );
    }

    if (
      !BULK_ALLOWED_CATEGORIES.includes(
        category
      )
    ) {
      errors.push(
        `Item ${number}: choose Free, Clothing, Traditional Wear, or Toys.`
      );
    }

    if (!item.location) {
      errors.push(
        `Item ${number}: location is required.`
      );
    }

    if (!item.description?.trim()) {
      errors.push(
        `Item ${number}: description is required.`
      );
    }

    if (category !== "free") {
      const price =
        Number(item.price);

      if (
        item.price === "" ||
        !Number.isFinite(price) ||
        price <= 0
      ) {
        errors.push(
          `Item ${number}: enter a price greater than $0, or use the Free category.`
        );
      }
    }

    if (
      (item.images || []).length >
      MAX_IMAGES
    ) {
      errors.push(
        `Item ${number}: maximum ${MAX_IMAGES} photos.`
      );
    }
  });

  return errors;
};


const handleBulkUpload = async () => {
  if (
    !isSignedIn ||
    !user?.token
  ) {
    setNotice({
      type: "error",
      text: "Please sign in before bulk uploading.",
    });

    return;
  }

 

  const errors =
    validateBulkItems();

  setBulkErrors(errors);

  if (errors.length) {
    setNotice({
      type: "error",
      text: `Please fix ${errors.length} bulk upload issue${
        errors.length === 1
          ? ""
          : "s"
      } first.`,
    });

    return;
  }

  const createdRecords = [];

  try {
    setIsBulkUploading(true);

    setBulkProgress({
      current: 0,
      total: bulkItems.length,
    });

    for (
      let index = 0;
      index < bulkItems.length;
      index += 1
    ) {
      const item =
        bulkItems[index];

      const catName =
        normalizeCategoryName(
          item.category
        );

      const catId =
        getCatIdByName(catName);

      if (catId == null) {
        throw new Error(
          `Item ${index + 1}: invalid category.`
        );
      }

      /*
       * Do NOT send browser File objects
       * inside the JSON document.
       */
      const {
        images: itemImages = [],
        id: localBulkId,
        ...itemData
      } = item;

      const payload = {
        ...defaultListingTemplate,
        ...itemData,

        email: user.email,
        contactEmail: user.email,

        category: catName,

        price:
          catName === "free"
            ? 0
            : Number(item.price),

        location:
          item.location ||
          CITY_OPTIONS[0],

        lat:
          userLocation.lat,

        lon:
          userLocation.lon,

        seller: "User",

        contact: {
          ...defaultListingTemplate.contact,
        },
      };

      const created =
        await insertListing(
          catId,
          payload,
          user.token
        );

      if (
        created?.authenticated !==
          true ||
        created?.verified !== true
      ) {
        throw new Error(
          `Item ${index + 1}: the server did not publish the signed-in listing immediately.`
        );
      }

      let postId =
        created?.id ||
        created?.postid ||
        created?.postId ||
        created?.insertedId ||
        created?._id;

      if (
        postId &&
        typeof postId === "object"
      ) {
        postId =
          postId.$oid ||
          postId.toString?.();
      }

      if (!postId) {
        throw new Error(
          `Item ${index + 1}: listing was created but no post ID was returned.`
        );
      }

      /*
       * Record it BEFORE image upload.
       * If an image fails, we can securely
       * roll the created listing back.
       */
      createdRecords.push({
        catId,
        postId:
          String(postId),
      });

      const uploaded =
        itemImages.length
          ? await uploadImagesWithPostId(
              postId,
              itemImages,
              catId
            )
          : [];

      saveImagesForPost(
        postId,
        uploaded
      );

       setBulkProgress({
        current: index + 1,
        total: bulkItems.length,
      });
    }

    await sendHubMartSelfNotification(
      user.token,
      {
        type: "hubmart.bulk_published",
        count: createdRecords.length,
      }
    );

    setNotice({
      type: "success",
      text: `Successfully published ${bulkItems.length} HubMart listing${
        bulkItems.length === 1
          ? ""
          : "s"
      }.`,
    });

    setBulkItems([
      createBulkItem(),
    ]);

    setBulkErrors([]);

    setShowBulkForm(false);

    /*
     * Clear marketplace caches so newly
     * uploaded items are fetched again.
     */
    setAllBase([]);
    setPagination(
      createEmptyPagination()
    );
  } catch (error) {
    /*
     * Roll back anything this batch
     * already created so a failed batch
     * does not leave random partial posts.
     */
    const rollbackFailures = [];

    for (
      const record of
        [...createdRecords].reverse()
    ) {
      try {
        await deleteHubMartListing(
          record.catId,
          record.postId,
          user.token
        );
      } catch {
        rollbackFailures.push(
          record.postId
        );
      }
    }

    setNotice({
      type: "error",
      text:
        rollbackFailures.length
          ? `Bulk upload stopped: ${
              error?.message ||
              "upload failed"
            }. Some rollback cleanup also failed.`
          : `Bulk upload stopped and created listings were rolled back. ${
              error?.message ||
              ""
            }`,
    });
  } finally {
    setIsBulkUploading(false);

    setBulkProgress({
      current: 0,
      total: 0,
    });
  }
};
 const openBulkForm = () => {
  if (!isSignedIn) {
    setNotice({
      type: "error",
      text: "Please sign in to bulk upload HubMart listings.",
    });

    window.setTimeout(() => {
      window.location.href = SIGN_IN_ROUTE;
    }, 500);

    return;
  }

  if (
    typeof window !== "undefined" &&
    window.location.hash
  ) {
    window.location.hash = "";
    setHash("");
  }

  setShowPostForm(false);
  setShowBulkForm(true);

  if (isMobile) {
    setMobileStage("browse");
  }
};


const openPostForm = () => {
  setShowBulkForm(false);

  const nextCategory =
    !isVirtualCategory(activeCategory)
      ? normalizeCategoryName(activeCategory)
      : "";

  setDraft((prev) => ({
    ...prev,
    category:
      prev.category || nextCategory,
    location:
      prev.location ||
      filterCity ||
      CITY_OPTIONS[0] ||
      "",
  }));

  setShowPostForm(true);

  if (isMobile) {
    setMobileStage("browse");
  }
};


const goDonateFree = () => {
  setShowBulkForm(false);

  setDraft({
    ...defaultListingTemplate,

    category: "free",

    // Donate is always free.
    price: "0",

    location:
      filterCity ||
      CITY_OPTIONS[0] ||
      "",
  });

  setImages([]);

  setShowPostForm(true);

  if (isMobile) {
    setMobileStage("browse");
  }
};

  

  const handlePost = async () => {
    const catName = normalizeCategoryName(draft.category);
    const catId = getCatIdByName(catName);
    const validationIssues = getListingValidationIssues(draft, isSignedIn);

    if (validationIssues.length) {
      return setNotice({
        type: "error",
        text: validationIssues[0].message,
      });
    }

    try {
      setIsPosting(true);

      const ownerEmail = isSignedIn
        ? user.email
        : draft.contactEmail.trim();

      const payload = buildListingPayload({
        draft,
        categoryName: catName,
        ownerEmail,
        userLocation,
      });

      const created = await insertListing(
        catId,
        payload,
        isSignedIn ? user.token : ""
      );

      const publishedImmediately = Boolean(
        created?.authenticated === true &&
        created?.verified === true
      );

      let postId =
        created?.id ||
        created?.postid ||
        created?.postId ||
        created?.insertedId ||
        created?._id;

      if (postId && typeof postId === "object") {
        postId = postId.$oid || postId.toString?.();
      }

      if (!postId) {
        throw new Error(`Insert succeeded but no usable id found: ${JSON.stringify(created)}`);
      }

      const uploaded = images.length
        ? await uploadImagesWithPostId(postId, images, catId)
        : [];

      saveImagesForPost(postId, uploaded);

      if (publishedImmediately && user?.token) {
        await sendHubMartSelfNotification(
          user.token,
          {
            type: "hubmart.published",
            itemTitle: draft.title.trim(),
            entityId: String(postId),
          }
        );
      }

      const localListing = {
        ...payload,
        id: postId,
        postid: postId,
        categoryId: catId,
        images: uploaded,
        image: uploaded[0] || "",
      };

      if (
        publishedImmediately &&
        normalizeCategoryName(activeCategory) === catName &&
        pagination.page === 1
      ) {
        setAllItems((prev) => dedupeListings([localListing, ...prev]).slice(0, PAGE_LIMIT));
        setPagination((prev) => ({
          ...prev,
          totalCount: Number(prev.totalCount || 0) + 1,
          totalPages: getTotalPages(Number(prev.totalCount || 0) + 1),
        }));
      }

      setNotice({
        type: "success",
        text: publishedImmediately
          ? "Published successfully. Your listing is live now."
          : "Successfully submitted. Your listing will be published once you verify your email. Please check your inbox or spam/junk folder for the verification email.",
      });

      setDraft({ ...defaultListingTemplate, location: filterCity || CITY_OPTIONS[0] || "" });
      setImages([]);
      setShowPostForm(false);
    } catch (err) {
      setNotice({ type: "error", text: err?.message || "Error posting listing." });
    } finally {
      setIsPosting(false);
    }
  };

  const activeHeading = activeCategory ? categoryLabel(activeCategory) : "Marketplace";
  const draftCategoryKey = normalizeCategoryName(draft.category || activeCategory || "");
  const formConfig = getCategoryFormConfig(draftCategoryKey);

  return (
    <div className="App">
      <Template variant="market">
        <div className="headerSection">
          <Header onSearch={(query) => setSearch(query || "")} />
        </div>

<div className="menuSection" />

<div className="mainSection">

  {!detailId &&
    !showPostForm &&
    !showBulkForm && (
      <div className="hb-top-action-row">
        <button
          type="button"
          className="hb-top-post-btn"
          onClick={() => {
            if (
              typeof window !== "undefined" &&
              window.location.hash
            ) {
              window.location.hash = "";
              setHash("");
            }

            openPostForm();
          }}
        >
          <FaPlus aria-hidden="true" />
          <span>Post</span>
        </button>
      </div>
    )}


    

          {detailId ? (
            <div className="hubmart-container">
             <ItemDetail
  id={detailId}
  allItems={allItems}
  user={user}
  onBack={() => {
    window.location.hash = "";
  }}
  onRequireSignIn={() => {
    window.location.href =
      SIGN_IN_ROUTE;
  }}
  onDeleted={(deletedId) => {
    setAllItems((current) =>
      current.filter(
        (listing) =>
          getBackendAdId(listing) !==
          String(deletedId)
      )
    );

   

    setNotice({
      type: "success",
      text: "Listing deleted successfully.",
    });

    window.location.hash = "";
  }}
/>
            </div>
          ) : (
            <div className="hubmart-container">
              {!showPostForm &&
  !showBulkForm &&
  (!isMobile ||
    mobileStage === "browse") && (
                <MarketplaceHeader
                  filterCity={filterCity}
                  setFilterCity={setFilterCity}
                  activeCategory={activeCategory}
                  setActiveCategory={handleCategoryPick}
                  search={search}
                  setSearch={setSearch}
                  onSearchSubmit={onSearchSubmit}
                  showFilters={showFilters}
                  setShowFilters={setShowFilters}
                  isMobile={isMobile}
                  searchStatus={searchStatus}
                  onMobileLandingPickCategory={handleCategoryPick}
                  onHeroPost={openPostForm}
onHeroDonate={goDonateFree}
onHeroBulk={openBulkForm}
                  aiQuery={aiQuery}
                  setAiQuery={setAiQuery}
                  onAiSearch={handleAiSearch}
                  aiSearchState={aiSearchState}
                />
              )}

              <NoticeBanner notice={notice} onClose={() => setNotice(null)} />

              {isMobile && (
                <>
                  <MobileFilterSheet
                    open={mobileFilterOpen}
                    onClose={() => setMobileFilterOpen(false)}
                    activeCategory={activeCategory}
                    priceMin={priceMin}
                    priceMax={priceMax}
                    setPriceMin={setPriceMin}
                    setPriceMax={setPriceMax}
                    filterCondition={filterCondition}
                    setFilterCondition={setFilterCondition}
                    onApply={handlePriceApply}
                    onClearAndReset={handlePriceClear}
                  />
                  <MobileSortSheet
                    open={mobileSortOpen}
                    onClose={() => setMobileSortOpen(false)}
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    onAfterChange={handleMobileSortChange}
                    activeCategory={activeCategory}
                  />
                </>
              )}

              {showPostForm && (
                <div className="hb-backbar">
                  <button
                    type="button"
                    className="hb-btn hb-btn--ghost"
                    onClick={() => setShowPostForm(false)}
                  >
                    ← Back to marketplace
                  </button>
                </div>
              )}
              {showBulkForm && (
  <div className="hb-bulk-wrap">
    <section className="hb-panel hb-bulk-panel">

      <div className="hb-section-heading">
        <p className="hb-section-heading__eyebrow">
          Multiple Upload
        </p>

        <h2 className="hb-section-heading__title">
          Add up to {MAX_BULK_ITEMS} listings
        </h2>
      </div>

      <div className="hb-bulk-topbar">
        <div>
          <strong>
            {bulkItems.length} / {MAX_BULK_ITEMS}
          </strong>{" "}
          listings
        </div>

        <div className="hb-inline-status hb-inline-status--ok">
          Signed in as {user?.email}
        </div>
      </div>

      <p className="hb-bulk-intro">
        Bulk upload currently supports Free,
Clothing, Traditional Wear, and Toys. Each listing can
        include up to {MAX_IMAGES} photos.
      </p>

      <div className="hb-bulk-items">
        {bulkItems.map(
          (item, itemIndex) => (
            <article
              key={item.id}
              className="hb-bulk-item"
            >
              <div className="hb-bulk-item__head">
                <div>
                  <span className="hb-badge hb-badge--soft">
                    Item {itemIndex + 1}
                  </span>

                  <h3>
                    {item.title ||
                      `Listing ${
                        itemIndex + 1
                      }`}
                  </h3>
                </div>

                {bulkItems.length >
                  1 && (
                  <button
                    type="button"
                    className="hb-bulk-remove"
                    onClick={() =>
                      removeBulkItem(
                        item.id
                      )
                    }
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className="hb-form-grid">
                <Field
                  name={`bulk-title-${item.id}`}
                  label="Title"
                  value={item.title}
                  onChange={(_, value) =>
                    handleBulkItemChange(
                      item.id,
                      "title",
                      value
                    )
                  }
                  placeholder="Kids jacket"
                />

                <Field
                  name={`bulk-category-${item.id}`}
                  label="Category"
                  type="select"
                  options={
                    BULK_ALLOWED_CATEGORIES.map(
                      (category) => ({
                        value:
                          category,
                        label:
                          categoryLabel(
                            category
                          ),
                      })
                    )
                  }
                  value={item.category}
                  onChange={(_, value) =>
                    handleBulkItemChange(
                      item.id,
                      "category",
                      value
                    )
                  }
                />

                {item.category !==
                  "free" && (
                  <Field
                    name={`bulk-price-${item.id}`}
                    label="Price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={item.price}
                    onChange={(_, value) =>
                      handleBulkItemChange(
                        item.id,
                        "price",
                        value
                      )
                    }
                    placeholder="0.00"
                  />
                )}

                <div className="hb-form-group">
                  <label className="hb-form-label">
                    Location
                  </label>

                  <select
                    className="hb-input"
                    value={
                      item.location
                    }
                    onChange={(event) =>
                      handleBulkItemChange(
                        item.id,
                        "location",
                        event.target
                          .value
                      )
                    }
                  >
                    {CITY_OPTIONS.map(
                      (city) => (
                        <option
                          key={city}
                          value={city}
                        >
                          {city}
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>

              {/* CLOTHING */}
            {["cloth", "traditional"].includes(
  item.category
) && (
                <div className="hb-form-grid hb-bulk-category-fields">
                  <Field
                    name={`bulk-brand-${item.id}`}
                    label="Brand"
                    value={item.brand}
                    onChange={(_, value) =>
                      handleBulkItemChange(
                        item.id,
                        "brand",
                        value
                      )
                    }
                  />

                  <Field
                    name={`bulk-gender-${item.id}`}
                    label="Gender"
                    type="select"
                    options={[
                      "Men",
                      "Women",
                      "Unisex",
                      "Kids",
                      "Baby",
                    ]}
                    value={item.gender}
                    onChange={(_, value) =>
                      handleBulkItemChange(
                        item.id,
                        "gender",
                        value
                      )
                    }
                  />

                  <Field
                    name={`bulk-size-${item.id}`}
                    label="Size"
                    value={item.size}
                    onChange={(_, value) =>
                      handleBulkItemChange(
                        item.id,
                        "size",
                        value
                      )
                    }
                  />

                  <Field
                    name={`bulk-color-${item.id}`}
                    label="Color"
                    value={item.color}
                    onChange={(_, value) =>
                      handleBulkItemChange(
                        item.id,
                        "color",
                        value
                      )
                    }
                  />

                  <Field
                    name={`bulk-material-${item.id}`}
                    label="Material"
                    value={item.material}
                    onChange={(_, value) =>
                      handleBulkItemChange(
                        item.id,
                        "material",
                        value
                      )
                    }
                  />
                </div>
              )}

              {/* TOYS */}
              {item.category ===
                "toys" && (
                <div className="hb-form-grid hb-bulk-category-fields">
                  <Field
                    name={`bulk-brand-${item.id}`}
                    label="Brand"
                    value={item.brand}
                    onChange={(_, value) =>
                      handleBulkItemChange(
                        item.id,
                        "brand",
                        value
                      )
                    }
                  />

                  <Field
                    name={`bulk-age-${item.id}`}
                    label="Age range"
                    value={
                      item.ageRange
                    }
                    onChange={(_, value) =>
                      handleBulkItemChange(
                        item.id,
                        "ageRange",
                        value
                      )
                    }
                    placeholder="3-5 yrs"
                  />
                </div>
              )}

              <div className="hb-form-grid hb-bulk-category-fields">
                <Field
                  name={`bulk-condition-${item.id}`}
                  label="Condition"
                  type="select"
                  options={
                    ["cloth", "traditional"].includes(
  item.category
)
                      ? [
                          "New with tags",
                          "New without tags",
                          "Like New",
                          "Good",
                          "Fair",
                        ]
                      : [
                          "New",
                          "Like New",
                          "Good",
                          "Fair",
                        ]
                  }
                  value={
                    item.condition
                  }
                  onChange={(_, value) =>
                    handleBulkItemChange(
                      item.id,
                      "condition",
                      value
                    )
                  }
                />
              </div>

              <div className="hb-bulk-description">
                <Field
                  name={`bulk-description-${item.id}`}
                  label="Description"
                  type="textarea"
                  value={
                    item.description
                  }
                  onChange={(_, value) =>
                    handleBulkItemChange(
                      item.id,
                      "description",
                      value
                    )
                  }
                  placeholder="Describe the item, condition, pickup details, defects, or anything the buyer should know."
                />
              </div>

              <div className="hb-bulk-photo-section">
                <label
                  className="hb-upload-box hb-upload-box--bulk"
                  htmlFor={`bulk-images-${item.id}`}
                >
                  <input
                    id={`bulk-images-${item.id}`}
                    className="hb-upload-input"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(event) =>
                      handleBulkImagesSelect(
                        item.id,
                        event
                      )
                    }
                  />

                  <span className="hb-upload-box__icon">
                    +
                  </span>

                  <strong>
                    Add up to {MAX_IMAGES} photos
                  </strong>

                  <span>
                    {item.images.length} /{" "}
                    {MAX_IMAGES} selected
                  </span>
                </label>

                {item.images.length >
                  0 && (
                  <div className="hb-bulk-photo-grid">
                    {item.images.map(
                      (
                        file,
                        imageIndex
                      ) => (
                        <BulkPhotoThumb
                          key={`${file.name}-${file.size}-${imageIndex}`}
                          file={file}
                          onRemove={() =>
                            removeBulkImage(
                              item.id,
                              imageIndex
                            )
                          }
                        />
                      )
                    )}
                  </div>
                )}
              </div>
            </article>
          )
        )}
      </div>

      <div className="hb-bulk-add-row">
        <button
          type="button"
          className="hb-btn hb-btn--ghost"
          onClick={addBulkItem}
          disabled={
            bulkItems.length >=
            MAX_BULK_ITEMS
          }
        >
          <FaPlus aria-hidden="true" />

          {bulkItems.length >=
          MAX_BULK_ITEMS
            ? "25 item limit reached"
            : "Add another item"}
        </button>
      </div>

      <div className="hb-publish-bar">
        <button
          type="button"
          className="hb-btn hb-btn--ghost"
          onClick={() => {
            setShowBulkForm(false);
          }}
        >
          ← Back to marketplace
        </button>

        <div className="hb-publish-bar__note">
          Nothing is uploaded until
          the entire batch passes
          validation.
          {bulkErrors.length > 0 && (
  <div className="hb-inline-status hb-inline-status--error">
    <strong>
      Please fix these items:
    </strong>

    <ul>
      {bulkErrors.map(
        (error, index) => (
          <li key={index}>
            {error}
          </li>
        )
      )}
    </ul>
  </div>
)}
        </div>

        <button
  type="button"
  className="hb-btn hb-btn--bulk"
  onClick={handleBulkUpload}
  disabled={isBulkUploading}
>
  {isBulkUploading
    ? `Uploading ${bulkProgress.current} of ${bulkProgress.total}…`
    : `Upload ${bulkItems.length} listing${
        bulkItems.length === 1
          ? ""
          : "s"
      }`}
</button>
      </div>
    </section>
  </div>
)}

             {!showPostForm &&
  !showBulkForm &&
  (!isMobile ||
    mobileStage === "results") && (
                <div
                  className={`hb-main-layout ${
                    showFilters && activeCategory && !isMobile ? "has-sidebar" : "no-sidebar"
                  } ${isMobile ? "is-mobile-market-layout" : ""}`}
                >
                  {!isMobile && (
                    <FiltersSidebar
                      show={showFilters && !!activeCategory}
                      activeCategory={activeCategory}
                      priceMin={priceMin}
                      priceMax={priceMax}
                      setPriceMin={setPriceMin}
                      setPriceMax={setPriceMax}
                      filterCondition={filterCondition}
                      setFilterCondition={setFilterCondition}
                      onPriceApply={handlePriceApply}
                      onPriceClear={handlePriceClear}
                    />
                  )}

                  <section className="hb-results-panel" ref={resultsRef}>
                    {!activeCategory ? (
                      <div className="hb-empty-state">
                        <h3>Pick a category to start.</h3>
                        <p>
                          No category means no signal. Choose one and the marketplace actually
                          becomes useful.
                        </p>
                      </div>
                    ) : isBootLoading ? (
                      <div className="hb-empty-state">
                        <h3>Loading listings…</h3>
                        <p>Pulling inventory for {activeHeading.toLowerCase()}.</p>
                      </div>
                    ) : (
                      <>
                        {isMobile && mobileStage === "results" && (
                          <div className="hb-mobile-results-topbar">
                            <button
                              type="button"
                              className="hb-mobile-results-back"
                              onClick={() => setMobileStage("browse")}
                            >
                              ← {activeCategory ? categoryLabel(activeCategory) : "Marketplace"}
                            </button>

                            <div className="hb-mobile-results-searchcard">
                              <label className="hb-form-label" htmlFor="hb-search-mobile-results">
                                Search in {activeCategory ? categoryLabel(activeCategory) : "marketplace"}
                              </label>
                              <div className="hb-mobile-results-searchrow">
                                <div className="hb-mobile-search-inputwrap">
                                  <FaSearch className="hb-mobile-search-icon" />
                                  <input
                                    id="hb-search-mobile-results"
                                    className="hb-input hb-mobile-search-input"
                                    type="text"
                                    placeholder={
                                      activeCategory
                                        ? `Search ${categoryLabel(activeCategory).toLowerCase()}…`
                                        : "Search listings…"
                                    }
                                    value={search}
                                    onChange={(event) => setSearch(event.target.value)}
                                    onKeyDown={(event) => {
                                      if (event.key === "Enter" && activeCategory) onSearchSubmit();
                                    }}
                                  />
                                </div>

                                <button
                                  type="button"
                                  className="hb-btn hb-btn--primary hb-mobile-search-btn"
                                  disabled={!activeCategory || isBootLoading}
                                  onClick={onSearchSubmit}
                                >
                                  {isBootLoading ? "Searching…" : "Search"}
                                </button>
                              </div>
                            </div>

                            <div className="hb-mobile-results-controls">
                              <span className="hb-mobile-results-city">{CITY_OPTIONS[0]}</span>

                              <button
                                type="button"
                                className="hb-mobile-results-chip"
                                onClick={() => setMobileFilterOpen(true)}
                              >
                                <FaSlidersH />
                                <span>Filters</span>
                              </button>

                              <button
                                type="button"
                                className="hb-mobile-results-chip"
                                onClick={() => setMobileSortOpen(true)}
                              >
                                <FaSortAmountDownAlt size={20} />
                                <span>{getSortLabel(sortBy)}</span>
                              </button>
                            </div>
                          </div>
                        )}

                        <div className="hb-results-head">
                          <div>
                            <p className="hb-results-head__eyebrow">Browse listings</p>
                            <h2 className="hb-results-head__title">{activeHeading}</h2>
                          </div>

                          <div className="hb-results-head__actions">
                            <div className="hb-results-count">
                              Total {pagination.totalCount || 0} item
                              {Number(pagination.totalCount || 0) === 1 ? "" : "s"}
                            </div>

                            {!isMobile && (
                              <div className="hb-sort">
                                <label className="hb-sort__label" htmlFor="hb-sort">
                                  Sort
                                </label>
                                <select
                                  id="hb-sort"
                                  className="hb-input hb-input--compact"
                                  value={sortBy}
                                  onChange={(event) => setSortBy(event.target.value)}
                                >
                                  {getSortOptionsForCategory(activeCategory).map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>
                        </div>

                        {filtered.length ? (
                          <div className="hb-listings-grid">
                            {filtered.map((listing, index) => (
                              <div
                                key={`${stableId(listing)}-${index}`}
                                role="button"
                                tabIndex={0}
                                className="hb-card-click"
                                onClick={() => openListingInSameTab(listing)}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") openListingInSameTab(listing);
                                }}
                              >
                                <ListingCard listing={listing} userLocation={userLocation} />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="hb-empty-state">
                            <h3>No listings matched.</h3>
                            <p>Try looser filters, a broader search, or a less picky mood.</p>
                          </div>
                        )}

                        <PaginationControls
                          pagination={pagination}
                          currentCount={filtered.length}
                          isLoading={isBootLoading}
                          onPageChange={handlePageChange}
                        />
                      </>
                    )}
                  </section>
                </div>
              )}

              {showPostForm &&
  !showBulkForm && (
                <div className="hb-compose-layout">
                  <section className="hb-panel">
                    <div className="hb-section-heading">
                      <p className="hb-section-heading__eyebrow">Create listing</p>
                      <h2 className="hb-section-heading__title">Post something worth clicking</h2>
                    </div>

                    <div className="hb-form-section">
                      <div className="hb-form-grid">
                        <Field
                          name="category"
                          label="Category"
                          type="select"
                          options={POSTABLE_CATEGORY_OPTIONS}
                          value={draft.category}
                          onChange={handleDraftChange}
                        />

                        <Field
                          name="title"
                          label={formConfig.titleLabel}
                          value={draft.title}
                          onChange={handleDraftChange}
                          placeholder={formConfig.titlePlaceholder}
                        />

                        {formConfig.showPrice && (
                          <Field
                            name="price"
                            label={formConfig.priceLabel}
                            type="number"
                            min="0"
                            step="0.01"
                            value={draft.price}
                            onChange={handleDraftChange}
                            placeholder={formConfig.pricePlaceholder}
                          />
                        )}

                        <div className="hb-form-group">
                          <label className="hb-form-label" htmlFor="hb-post-city">
                            Location
                          </label>
                          <select
                            id="hb-post-city"
                            className="hb-input"
                            required
                            value={draft.location || ""}
                            onChange={(event) => handleDraftChange("location", event.target.value)}
                          >
                            <option value="">Select city</option>
                            {CITY_OPTIONS.map((city) => (
                              <option key={city} value={city}>
                                {city}
                              </option>
                            ))}
                          </select>
                        </div>

                        {isSignedIn ? (
                          <div className="hb-form-group">
                            <label className="hb-form-label">Posting account</label>

                            <div className="hb-inline-status hb-inline-status--success">
                              Signed in as {user.email}. No additional email verification is needed.
                            </div>
                          </div>
                        ) : (
                          <Field
                            name="contactEmail"
                            label="Contact email"
                            type="email"
                            value={draft.contactEmail}
                            onChange={handleDraftChange}
                            placeholder="you@example.com"
                          />
                        )}
                      </div>
                    </div>

                    <div className="hb-form-section">
                      <div className="hb-section-heading">
                        <p className="hb-section-heading__eyebrow">Description</p>
                        <h2 className="hb-section-heading__title">
                          {formConfig.descriptionHeading}
                        </h2>
                      </div>

                      <Field
                        name="description"
                        label={formConfig.descriptionLabel}
                        type="textarea"
                        value={draft.description}
                        onChange={handleDraftChange}
                        placeholder={formConfig.descriptionPlaceholder}
                      />
                    </div>

                    <CategoryFields
                      category={draft.category || activeCategory}
                      draft={draft}
                      onChange={handleDraftChange}
                    />

                    <div className="hb-form-section">
                      <div className="hb-section-heading">
                        <p className="hb-section-heading__eyebrow">Photos</p>
                        <h2 className="hb-section-heading__title">{formConfig.photoHeading}</h2>
                      </div>

                      <label className="hb-upload-box" htmlFor="hb-upload-input">
                        <input
                          id="hb-upload-input"
                          className="hb-upload-input"
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImagesSelect}
                        />
                        <span className="hb-upload-box__icon">+</span>
                        <strong>Upload up to {MAX_IMAGES} photos</strong>
                        <span>{formConfig.photoHelp}</span>
                      </label>

                      {previewImageUrls.length > 0 && (
                        <div className="hb-upload-preview">
                          <ImageCarousel
                            images={previewImageUrls}
                            alt="Preview"
                            height={240}
                            fit="auto"
                          />

                          <div className="hb-thumb-grid">
                            {previewImageUrls.map((url, index) => (
                              <div key={`${url}-${index}`} className="hb-thumb-card">
                                <img
                                  src={url}
                                  alt={`upload-${index + 1}`}
                                  className="hb-thumb-card__img"
                                />
                                <button
                                  type="button"
                                  className="hb-thumb-card__remove"
                                  onClick={() => removeImageAt(index)}
                                >
                                  Remove
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="hb-form-section">
                      <div className="hb-section-heading">
                        <p className="hb-section-heading__eyebrow">Contact options</p>
                        <h2 className="hb-section-heading__title">{formConfig.contactHeading}</h2>
                      </div>

                      <div className="hb-checkbox-grid">
                        <label className="hb-checkbox">
                          <input
                            type="checkbox"
                            checked={!!draft.contact?.emailRelay}
                            onChange={(event) =>
                              handleContactChange("emailRelay", event.target.checked)
                            }
                          />
                          <span>Email relay</span>
                        </label>

                        <label className="hb-checkbox">
                          <input
                            type="checkbox"
                            checked={!!draft.contact?.showPhone}
                            onChange={(event) =>
                              handleContactChange("showPhone", event.target.checked)
                            }
                          />
                          <span>Show phone</span>
                        </label>

                        <label className="hb-checkbox">
                          <input
                            type="checkbox"
                            checked={!!draft.contact?.offeredInPerson}
                            onChange={(event) =>
                              handleContactChange("offeredInPerson", event.target.checked)
                            }
                          />
                          <span>In person</span>
                        </label>

                        <label className="hb-checkbox">
                          <input
                            type="checkbox"
                            checked={!!draft.contact?.offeredVirtual}
                            onChange={(event) =>
                              handleContactChange("offeredVirtual", event.target.checked)
                            }
                          />
                          <span>Virtual</span>
                        </label>
                      </div>
                    </div>

                    <div className="hb-publish-bar">
                      <div className="hb-publish-bar__note">
  {isSignedIn
    ? `Signed in as ${user.email}. Your listing will be published immediately.`
    : "After submitting, check your email. Your listing will be published once your email is verified."}
</div>
                      <button
                        className="hb-btn hb-btn--primary"
                        type="button"
                        onClick={handlePost}
                        disabled={isPosting}
                      >
                        {isPosting ? "Publishing..." : formConfig.publishLabel}
                      </button>
                    </div>
                  </section>

                  <aside className="hb-preview-panel">
                    <ListingCoach
                      draft={draft}
                      imageCount={images.length}
                      isSignedIn={isSignedIn}
                      previewListing={previewListing}
                      userLocation={userLocation}
                      isPosting={isPosting}
                      onPublish={handlePost}
                    />
                  </aside>
                </div>
              )}
            </div>
             )}
{!detailId &&
    !showPostForm &&
    !showBulkForm && (
      <MobileFeatureDeck />


          )}
        </div>

        <div className="rightNavSection" />
        <Footer />
      </Template>
    </div>
  );
}
